import { campaignDestination } from "./campaignAttribution";
import { MarketingControls } from "./MarketingControls";
import { FormEvent, ReactNode, useEffect, useState } from "react";
import {
  ArrowRight,
  CalendarDays,
  Check,
  CheckCircle2,
  CircleAlert,
  CircleX,
  Copy,
  ContactRound,
  DatabaseBackup,
  Globe2,
  FolderOpen,
  FileCheck2,
  KeyRound,
  LifeBuoy,
  LockKeyhole,
  Mail,
  MessageCircle,
  Menu,
  Radar,
  PhoneCall,
  Video,
  RotateCcw,
  Route as RouteIcon,
  Search,
  ServerCog,
  Settings2,
  ShieldCheck,
  SquareCheckBig,
  Users,
  X,
} from "lucide-react";
import { Link, Route as WouterRoute, Switch, useLocation } from "wouter";
import logoUrl from "../../brand-assets/envelaro-logo.png";
import collaborationBrandUrl from "./assets/collaboration-brand.png";
import { ORGANIZATION_PLANS } from "../../src/shared/organizationPlans";
import {
  emailHealthEnabled,
  launchListEnabled,
  recordWebsiteVisit,
  scanPublicEmailDns,
  submitLaunchLead,
  type EmailHealthFindingType,
  type EmailHealthResult,
} from "./firebase";
import {
  legalDocumentByPath,
  legalDocuments,
  type LegalDocument,
} from "./legalContent";
import { BlocklistCheckerPage, RecordCheckerPage } from "./FreeTools";
import { FAQ_SECTIONS } from "./faqContent";
import {
  RESOURCE_GUIDES,
  type ResourceGuide,
} from "./resourceGuides";
import { SiteMetadataManager } from "./SiteMetadata";
import { StructuredData } from "./StructuredData";
import { EvaluationPage } from "./EvaluationPage";
import {
  isAbsoluteHttpUrl,
  planPurchaseUrl,
  TRIAL_SIGNUP_URL,
} from "./conversionLinks";

const adminUrl =
  import.meta.env.VITE_ADMIN_URL || "https://envelaro-admin-dev.web.app";

const navItems = [
  { label: "Product", href: "/product" },
  { label: "Meet", href: "/product#envelaro-meet" },
  { label: "Pricing", href: "/pricing" },
  { label: "Security", href: "/security" },
  { label: "Migration", href: "/migration" },
  { label: "Email check", href: "/tools/email-health" },
  { label: "Resources", href: "/resources" },
];

function Brand({ inverted = false }: { inverted?: boolean }) {
  return (
    <Link className={`brand${inverted ? " brand--inverted" : ""}`} href="/">
      <img alt="Envelaro" className="brand__logo" src={logoUrl} />
    </Link>
  );
}

function ButtonLink({
  href,
  children,
  variant = "primary",
  external = false,
  onClick,
}: {
  href: string;
  children: ReactNode;
  variant?: "primary" | "secondary" | "text" | "light";
  external?: boolean;
  onClick?: () => void;
}) {
  const className = `button button--${variant}`;
  if (external || isAbsoluteHttpUrl(href)) {
    return (
      <a className={className} href={href} onClick={(event) => { event.currentTarget.href = campaignDestination(href); onClick?.(); }}>
        {children}
      </a>
    );
  }
  return (
    <Link className={className} href={href} onClick={onClick}>
      {children}
    </Link>
  );
}

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [location] = useLocation();

  return (
    <header className="site-header">
      <div className="container site-header__inner">
        <Brand />
        <nav className="desktop-nav" aria-label="Primary navigation">
          {navItems.map((item) => (
            <Link
              className={location === item.href ? "nav-link nav-link--active" : "nav-link"}
              href={item.href}
              key={item.href}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="site-header__actions">
          <a className="nav-link sign-in" href={adminUrl}>
            Log in
          </a>
          <ButtonLink href={TRIAL_SIGNUP_URL}>Start free trial</ButtonLink>
        </div>
        <button
          aria-expanded={menuOpen}
          aria-label={menuOpen ? "Close navigation" : "Open navigation"}
          className="menu-button"
          onClick={() => setMenuOpen((current) => !current)}
          type="button"
        >
          {menuOpen ? <X /> : <Menu />}
        </button>
      </div>
      {menuOpen ? (
        <nav className="mobile-nav" aria-label="Mobile navigation">
          <div className="container mobile-nav__inner">
            {navItems.map((item) => (
              <Link href={item.href} key={item.href} onClick={() => setMenuOpen(false)}>
                {item.label}
                <ArrowRight size={18} />
              </Link>
            ))}
            <a href={adminUrl}>Log in</a>
            <ButtonLink href={TRIAL_SIGNUP_URL} onClick={() => setMenuOpen(false)}>
              Start free trial
            </ButtonLink>
          </div>
        </nav>
      ) : null}
    </header>
  );
}

function EvaluationAnnouncement() {
  return (
    <aside className="evaluation-announcement" aria-label="Evaluation invitation">
      <div className="container evaluation-announcement__inner">
        <span>Invitation</span>
        <strong>Inviting small businesses to evaluate Envelaro</strong>
        <a href="/evaluation#apply">
          Request an evaluation <ArrowRight size={16} aria-hidden="true" />
        </a>
      </div>
    </aside>
  );
}

function RoutingLine({ dark = false }: { dark?: boolean }) {
  return (
    <svg
      aria-hidden="true"
      className={`routing-line${dark ? " routing-line--dark" : ""}`}
      viewBox="0 0 660 150"
    >
      <path d="M10 108h76c30 0 30-34 60-34h89c27 0 27 46 56 46h77c31 0 31-84 62-84h96" />
      <path d="M10 132h123c28 0 28-30 56-30h84" strokeDasharray="8 9" />
      <circle cx="10" cy="108" r="7" />
      <circle cx="235" cy="74" r="7" />
      <circle cx="430" cy="36" r="7" />
      <rect height="34" rx="8" width="42" x="526" y="19" />
      <path d="m537 30 10 8 10-8" />
    </svg>
  );
}

const suiteFeatures = [
  [Mail, "Webmail", "Professional email on your own domain"],
  [CalendarDays, "Calendar", "Shared schedules and meetings"],
  [SquareCheckBig, "Tasks", "Assigned work with clear ownership"],
  [ContactRound, "Contacts", "A connected business directory"],
  [MessageCircle, "Team chat", "Fast conversations in context"],
  [Video, "Envelaro Meet", "Video meetings with your team and invited guests"],
  [FolderOpen, "Storage", "Files available to the people who need them"],
  [Settings2, "Admin controls", "Domains, users and policy in one place"],
] as const;

function ProductPreview() {
  return (
    <div className="suite-constellation" aria-label="Managed business email operations">
      <div className="suite-constellation__halo" />
      <article className="suite-node suite-node--mail">
        <span><Mail aria-hidden="true" /></span>
        <div><small>Managed email</small><strong>Your domain is ready</strong><i /><i /><i /></div>
      </article>
      <article className="suite-node suite-node--calendar">
        <span><CalendarDays aria-hidden="true" /></span>
        <div><small>Cutover plan</small><strong>Mail routing scheduled</strong><p>Controlled change window</p></div>
      </article>
      <article className="suite-node suite-node--task">
        <span><SquareCheckBig aria-hidden="true" /></span>
        <div><small>Authentication</small><strong>SPF, DKIM and DMARC</strong><p>Verified before sending</p></div>
      </article>
      <article className="suite-node suite-node--chat">
        <span><MessageCircle aria-hidden="true" /></span>
        <div><small>Migration status</small><strong>Mailboxes synchronized</strong><p>Ready for final sync</p></div>
      </article>
      <article className="suite-node suite-node--contact">
        <span><ContactRound aria-hidden="true" /></span>
        <div><small>Migration support</small><strong>Specialist assigned</strong><p>Guided from start</p></div>
      </article>
      <article className="suite-node suite-node--call">
        <span><PhoneCall aria-hidden="true" /></span>
        <div><small>Human support</small><strong>Support check-in</strong><p>Specialist available</p></div>
      </article>
      <article className="suite-node suite-node--file">
        <span><FolderOpen aria-hidden="true" /></span>
        <div><small>Sign-off</small><strong>Cutover plan approved</strong><p>Ready to switch</p></div>
      </article>
      <svg className="suite-constellation__routes" viewBox="0 0 760 600" aria-hidden="true">
        <path d="M372 273C286 241 252 181 213 137" />
        <path d="M445 244C488 175 535 150 583 142" />
        <path d="M486 310C576 301 614 304 655 327" />
        <path d="M350 346C261 365 221 394 185 426" />
        <path d="M430 361C449 430 492 462 552 487" />
      </svg>
      <div className="suite-constellation__pulse suite-constellation__pulse--one" />
      <div className="suite-constellation__pulse suite-constellation__pulse--two" />
    </div>
  );
}

function SuiteBand() {
  return (
    <section className="suite-band">
      <div className="container suite-band__grid">
        <div className="suite-band__intro">
          <h2>Every workday tool, already connected.</h2>
          <p>Email is only the beginning. Envelaro keeps communication, commitments, people, video meetings and files in one managed workspace.</p>
          <ButtonLink href="/product" variant="text">Explore the full workspace <ArrowRight size={17} /></ButtonLink>
        </div>
        <div className="suite-band__features">
          {suiteFeatures.map(([Icon, title, copy]) => (
            <article key={title}>
              <Icon aria-hidden="true" />
              <div><p className="suite-band__feature-title">{title}</p><p>{copy}</p></div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function HomePage() {
  return (
    <main className="brand-home">
      <section className="hero">
        <div className="container hero__grid">
          <div className="hero__copy">
            <h1>Business email, properly <strong>managed.</strong></h1>
            <p>Business email on your domain, team chat and video meetings in one workspace. Bring your people together with guided setup, careful migration and clear administration.</p>
            <div className="button-row">
              <ButtonLink href={TRIAL_SIGNUP_URL}>Start free trial <ArrowRight size={17} /></ButtonLink>
              <ButtonLink href="/pricing" variant="secondary">View plans</ButtonLink>
            </div>
            <div className="hero__proof">
              <span><CalendarDays /> 7 business days</span>
              <span><SquareCheckBig /> No card required</span>
              <span><Users /> Up to 3 trial mailboxes</span>
            </div>
          </div>
          <img className="brand-collaboration" src={collaborationBrandUrl} alt="People connecting and working together from their own workspaces" width="1374" height="1145" fetchPriority="high" />
        </div>
      </section>
      <SuiteBand />
      <FeatureShowcase />
      <MeetOffering />
      <Workflow />
      <RoleOutcomes />
      <MigrationBand />
      <AccessAnywhere />
      <PlansPreview />
      <TrialBand />
      <SecurityBand />
      <HomeFaq />
      <FinalCta />
    </main>
  );
}
function FeatureShowcase() {
  const stories = [
    {
      id: "email",
      label: "Email & calendar",
      icon: Mail,
      title: "Your inbox should move work forward.",
      copy: "Use professional email on your domain, turn messages into meetings, and keep contacts close without switching between disconnected services.",
      points: ["Focused webmail", "Shared calendars", "Business contacts", "Domain identity controls"],
      visual: [Mail, CalendarDays, ContactRound],
    },
    {
      id: "conversation",
      label: "Chat & video meetings",
      icon: MessageCircle,
      title: "Choose the conversation that fits the moment.",
      copy: "Move a question from email to team chat or a video meeting while keeping your people in the same managed business workspace.",
      points: ["Team channels", "Direct conversations", "Voice and video meetings", "Business directory"],
      visual: [MessageCircle, Video, Users],
    },
    {
      id: "work",
      label: "Tasks & storage",
      icon: SquareCheckBig,
      title: "Keep commitments beside the conversation.",
      copy: "Assign work, track ownership and share the files a team needs without sending another attachment or losing the latest version.",
      points: ["Assigned tasks", "Due dates", "Shared files", "Team visibility"],
      visual: [SquareCheckBig, FolderOpen, CheckCircle2],
    },
    {
      id: "admin",
      label: "Security & admin",
      icon: ShieldCheck,
      title: "Give administrators clarity, not complexity.",
      copy: "Provision domains and mailboxes, guide email authentication, manage access and review activity from one operational surface.",
      points: ["Role-based administration", "SPF, DKIM and DMARC guidance", "Activity visibility", "Responsible sending controls"],
      visual: [ShieldCheck, KeyRound, Settings2],
    },
  ] as const;
  const [selected, setSelected] = useState(stories[0].id as (typeof stories)[number]["id"]);
  const story = stories.find((item) => item.id === selected) ?? stories[0];
  return (
    <section className="feature-showcase">
      <div className="container">
        <div className="section-heading section-heading--wide">
          <div><h2>One suite. Fewer broken handoffs.</h2></div>
          <p>Explore how Envelaro connects the tools surrounding business email.</p>
        </div>
        <div className="feature-showcase__tabs" role="tablist" aria-label="Envelaro suite features">
          {stories.map(({ id, label, icon: Icon }) => (
            <button key={id} type="button" role="tab" aria-selected={story.id === id} className={story.id === id ? "is-active" : ""} onClick={() => setSelected(id)}>
              <Icon />{label}
            </button>
          ))}
        </div>
        <div className="feature-showcase__panel" role="tabpanel">
          <div>
            <p className="feature-showcase__title">{story.title}</p>
            <p>{story.copy}</p>
            <ul>{story.points.map((point) => <li key={point}><CheckCircle2 />{point}</li>)}</ul>
          </div>
          <div className={`feature-scene feature-scene--${story.id}`} aria-hidden="true">
            {story.visual.map((Icon, index) => <span key={index}><Icon /></span>)}
            <i /><i /><i />
          </div>
        </div>
      </div>
    </section>
  );
}

function RoleOutcomes() {
  const roles = [
    ["For business owners", "Run the workday from one place.", "Give the team professional tools without assembling and paying for a patchwork of separate services."],
    ["For IT administrators", "Provision with fewer unknowns.", "Keep domains, users, authentication guidance and service activity visible through a focused control plane."],
    ["For IT directors", "Build on accountable boundaries.", "Use role-based administration, documented service practices and a clear compliance roadmap for growing operations."],
  ];
  return (
    <section className="role-outcomes">
      <div className="container">
        <div className="role-outcomes__heading"><h2>Different responsibilities. One operational picture.</h2><p>Envelaro gives every decision-maker the level of clarity they need.</p></div>
        <div className="role-outcomes__list">
          {roles.map(([label, title, copy], index) => <article key={label}><span>0{index + 1}</span><small>{label}</small><p className="role-outcomes__title">{title}</p><p>{copy}</p></article>)}
        </div>
      </div>
    </section>
  );
}

function AccessAnywhere() {
  return (
    <section className="access-anywhere">
      <div className="container access-anywhere__grid">
        <div>
          <h2>Great work happens everywhere.</h2>
          <p>Keep your email, conversations, and files close—wherever your day takes you.</p>
          <ul>
            <li><CheckCircle2 />Your business email, in your browser</li>
            <li><CheckCircle2 />Stay connected with your team</li>
            <li><CheckCircle2 />Bring your calendar, contacts, and files along</li>
          </ul>
          <ButtonLink href="/product">Explore the workspace <ArrowRight size={20} /></ButtonLink>
        </div>
        <img className="work-anywhere-art" src="/brand/work-anywhere.png" alt="People working with a laptop at a sunny café, a tablet in a studio, and a phone at home" width="1254" height="1254" loading="lazy" />
      </div>
    </section>
  );
}
function HomeFaq() {
  const questions = [
    ["Can I use my existing business domain?", "Yes. You keep ownership of your domain and verify it before Envelaro publishes or requests mail-routing changes."],
    ["What is included beyond email?", "Every organization plan includes webmail, calendar, tasks, contacts, team chat, voice calls, Envelaro Meet Lite video meetings and shared storage."],
    ["Can we move from another email provider?", "Yes. Migration planning covers domains, mailboxes, aliases, data movement, DNS preparation, validation and cutover."],
    ["How does the 7-business-day trial work?", "After verifying one domain and configuring the first mailbox, an administrator explicitly starts the card-free trial. Up to three mailbox users receive full workspace access. Weekends remain accessible but do not reduce the seven business days. Each mailbox can reach up to five external recipients per day."],
    ["Can administrators control domains and users?", "Yes. The tenant portal is designed for domain verification, mailbox provisioning, aliases, groups, access controls, service activity and billing administration."],
  ];
  return (
    <section className="home-faq">
      <div className="container home-faq__grid">
        <div><h2>Questions teams ask before they move.</h2><p>Clear answers about the product, migration, trial and trust posture.</p><ButtonLink href="/resources" variant="text">Visit resources <ArrowRight size={17} /></ButtonLink></div>
        <div>{questions.map(([question, answer]) => <details key={question}><summary>{question}<span>+</span></summary><p>{answer}</p></details>)}</div>
      </div>
    </section>
  );
}

function Workflow() {
  const steps = [
    [Globe2, "Add your domain", "Connect your existing business domain to Envelaro."],
    [FileCheck2, "Verify DNS", "Add the required DNS records to confirm ownership and enable email."],
    [Mail, "Create mailboxes", "Set up mailboxes for your team with the right names and permissions."],
    [Users, "Invite your team", "Add your team members and get everyone ready to start using Envelaro."],
  ] as const;
  return (
    <section className="workflow">
      <div className="container">
        <div className="section-heading section-heading--wide">
          <h2>Set up correctly before the first mailbox goes live.</h2>
          <p>
            Envelaro keeps each step visible, so nothing important gets lost
            between signup and first send.
          </p>
        </div>
        <div className="workflow__steps">
          {steps.map(([Icon, label, description], index) => (
            <article key={label}>
              <div className="workflow__step-visual"><span className="step-number">{index + 1}</span><span className="workflow__icon"><Icon aria-hidden="true" /></span></div>
              <p className="workflow__step-title">{label}</p>
              <p className="workflow__step-description">{description}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function TrialBand() {
  return (
    <section className="trial-band">
      <div className="container trial-band__grid">
        <div className="trial-band__content">
          <span>7-business-day trial</span>
          <h2>Run Envelaro with your team before you commit.</h2>
          <p>Experience business email, calendar, contacts, tasks, chat, voice and video meetings and storage in a working tenant. Configure your domain, create mailboxes and see how administration fits your business.</p>
          <ButtonLink href={TRIAL_SIGNUP_URL}>Start free trial</ButtonLink>
        </div>
        <div className="trial-band__card">
          <div className="trial-band__number"><strong>7</strong><span>business days</span></div>
          <div className="trial-band__terms">
            <div><strong>Full workspace</strong><span>Experience every core workday tool</span></div>
            <div><strong>Up to 3 mailboxes</strong><span>Invite a small evaluation team</span></div>
            <div><strong>Video meetings included</strong><span>1 host + 5 guests · 60 minutes per meeting</span></div>
            <div><strong>Controlled external sending</strong><span>Up to 5 external recipients per mailbox each day</span></div>
          </div>
          <p>Meet Lite includes 600 participant-minutes per organization each month. Trial email sending limits also apply.</p>
        </div>
      </div>
    </section>
  );
}
function MigrationBand() {
  return (
    <section className="migration-band">
      <div className="container migration-band__grid">
        <div>
          <h2>Move your email without making migration your IT project.</h2>
          <p>
            Plan a guided move from your current provider with mailbox mapping,
            DNS preparation and a clear cutover checklist.
          </p>
          <ButtonLink href="/migration" variant="light">
            Plan your migration <ArrowRight size={18} />
          </ButtonLink>
        </div>
        <div className="migration-checklist">
          <div className="migration-checklist__title">
            <RouteIcon size={19} /> Migration checklist
          </div>
          {[
            "Map mailboxes and aliases",
            "Prepare DNS records",
            "Validate SPF, DKIM and DMARC",
            "Plan the cutover window",
            "Switch MX and monitor",
          ].map((item, index) => (
            <div className="migration-row" key={item}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <i />
              <p>{item}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const pricingPlanMarketing = {
  starter: {
    descriptor: "For small teams",
    lead: "A focused starting point for small businesses moving to professional email.",
    extraFeatures: [
      "10 shared aliases and distribution-list addresses",
      "Secure IMAP and SMTP access",
      "Self-guided setup",
    ],
    featured: false,
    catchAll: null,
  },
  growth: {
    descriptor: "For growing teams",
    lead: "More capacity for established teams managing several business domains.",
    extraFeatures: [
      "50 shared aliases and distribution-list addresses",
      "Secure IMAP and SMTP access",
      "Priority onboarding guidance",
    ],
    featured: false,
    catchAll: null,
  },
  business: {
    descriptor: "Best value",
    lead: "High-capacity business email for larger organizations and expanding operations.",
    extraFeatures: [
      "100 shared aliases and distribution-list addresses",
      "Guided onboarding",
      "Migration planning included",
    ],
    featured: true,
    catchAll: "One catch-all destination per domain",
  },
} as const;

const pricingPlans = ORGANIZATION_PLANS.map((plan) => {
  const marketing = pricingPlanMarketing[plan.id];
  return {
    ...plan,
    ...marketing,
    metrics: [
      [String(plan.mailboxLimit), "mailboxes"],
      [String(plan.domainLimit), "custom domains"],
      [`${plan.storageGb} GB`, "shared mail storage"],
    ],
    features: [
      "Webmail on your business domain",
      "Calendar and shared scheduling",
      "Tasks and ownership tracking",
      "Business contacts directory",
      "Team chat and voice calls",
      "Envelaro Meet Lite included",
      "1 host + 5 guests · up to 60 minutes",
      "600 participant-minutes per organization/month",
      "Shared file storage",
      `Up to ${plan.mailboxLimit} active mailboxes`,
      `Up to ${plan.domainLimit} verified custom domains`,
      `${plan.storageGb} GB shared mail storage`,
      `${plan.dailyDeliveryLimit} external recipient deliveries per mailbox/day`,
      `${plan.maxRecipientsPerMessage} recipients per email, including expanded distribution lists`,
      `${plan.maxOutboundAttachmentMb} MB maximum outbound attachments`,
      ...marketing.extraFeatures,
    ],
  };
});

const pricingComparisonRows = [
  ["Envelaro Meet Lite", "Included", "Included", "Included", "Scoped"],
  ["People per meeting", "1 host + 5 guests", "1 host + 5 guests", "1 host + 5 guests", "Scoped"],
  ["Maximum meeting duration", "60 minutes", "60 minutes", "60 minutes", "Scoped"],
  ["Meet participant-minutes per organization/month", "600 shared", "600 shared", "600 shared", "Scoped"],
  ["Annual subscription", ...pricingPlans.map((plan) => `$${plan.priceAnnual.toLocaleString("en-US")}`), "Custom"],
  ["Active mailboxes", ...pricingPlans.map((plan) => `Up to ${plan.mailboxLimit}`), "100+"],
  ["Verified custom domains", ...pricingPlans.map((plan) => `Up to ${plan.domainLimit}`), "25+"],
  ["Shared mail storage", ...pricingPlans.map((plan) => `${plan.storageGb} GB`), "Custom"],
  ["External recipients", ...pricingPlans.map((plan) => `${plan.dailyDeliveryLimit} per mailbox/day`), "Scoped"],
  ["Recipients per message", ...pricingPlans.map((plan) => String(plan.maxRecipientsPerMessage)), "Scoped"],
  ["Outbound attachments", ...pricingPlans.map((plan) => `Up to ${plan.maxOutboundAttachmentMb} MB`), "Scoped"],
  ["Catch-all address", "Not included", "Not included", "One per domain", "Scoped"],
] as const;

const pricingQuestions = [
  ["How does the included video meeting allowance work?", "Starter, Growth and Scale include Envelaro Meet Lite: one host and up to five guests, up to 60 minutes per meeting, and 600 participant-minutes shared by the organization each month. Each connected participant uses minutes: six people connected for 30 minutes use 180 participant-minutes. This is not a per-mailbox allowance. When the allowance is exhausted, new joins are blocked until it resets."],
  ["Can people outside our organization join?", "Yes. Invite guests from your calendar and share their meeting invitation. Guests join in the browser using their invitation details and wait until the host arrives."],
  [
    "Is Envelaro priced per user?",
    "No. Public plans are priced per organization and include a published mailbox, domain and shared-storage capacity. The per-mailbox figure is only an equivalent cost when the plan is fully used.",
  ],
  [
    "What is included with every business email plan?",
    "Every public plan includes custom-domain webmail, secure IMAP and SMTP access, calendar, contacts, tasks, team chat, voice calls, Envelaro Meet Lite video meetings, shared files and Envelaro Protect.",
  ],
  [
    "Can we keep our existing business domain?",
    "Yes. Your organization keeps ownership of its domain. Envelaro guides verification, mail routing, SPF, DKIM and DMARC configuration for each approved sending domain.",
  ],
  [
    "Can Envelaro migrate email from our current provider?",
    "Migration is available after a source assessment. The method and scope depend on the provider, mailbox count, data volume, access model and any calendar, contact or archive requirements.",
  ],
] as const;

type PricingPlan = (typeof pricingPlans)[number];

const pricingProtectionFeatures = [
  "Spam and phishing filtering",
  "Antivirus and malware scanning",
  "TLS/SSL encrypted connections",
  "SPF, DKIM and DMARC guidance",
  "Account safeguards and abuse monitoring",
];

function PricingPlanCard({ plan }: { plan: PricingPlan }) {
  const standardMonthlyPrice = plan.name === "Starter" ? 49 : plan.name === "Growth" ? 149 : 249;
  return (
    <article className={`offer-card${plan.featured ? " offer-card--featured" : ""}`}>
      <div className="offer-card__topline">
        <span>{plan.name}</span>
        <span className={plan.featured ? "offer-card__badge" : "offer-card__descriptor"}>
          {plan.descriptor}
        </span>
      </div>
      <div className="offer-card__price">
        <div className="offer-card__price-values">
          <strong><sup>$</sup>{plan.priceAnnual / 12}</strong>
          <s><sup>$</sup>{standardMonthlyPrice}</s>
        </div>
        <span>per month<br />billed ${plan.priceAnnual.toLocaleString("en-US")} annually</span>
      </div>
      <p className="offer-card__mailbox-equivalent">
        As low as ${(plan.priceAnnual / 12 / plan.mailboxLimit).toFixed(2)} per included mailbox/month when fully utilized.
      </p>
      <p className="offer-card__lead">{plan.lead}</p>
      <p className="offer-card__protect">
        <ShieldCheck size={15} /> Envelaro Protect included
      </p>
      <div className="offer-card__actions">
        <ButtonLink href={TRIAL_SIGNUP_URL}>
          Start free trial <ArrowRight size={18} />
        </ButtonLink>
        <ButtonLink href={planPurchaseUrl(plan.id)} variant="secondary">
          Buy {plan.name} now
        </ButtonLink>
      </div>
      <small className="offer-card__decision-note">
        Trial requires no card and never auto-renews. Buying starts annual billing immediately. Prices are in USD.
      </small>
      <div className="offer-card__metrics">
        {plan.metrics.map(([value, label]) => (
          <div key={label}><strong>{value}</strong><span>{label}</span></div>
        ))}
      </div>
      <ul className="offer-card__features">
        <li className="offer-card__feature-heading">Plan capacity</li>
        {plan.features.slice(9).map((item) => (
          <li key={item}><CheckCircle2 size={18} />{item}</li>
        ))}
        <li className={plan.catchAll ? "offer-card__catch-all" : "offer-card__catch-all offer-card__catch-all--unavailable"}>
          {plan.catchAll ? <CheckCircle2 size={18} /> : <span aria-hidden="true">&mdash;</span>}
          <span><strong>Catch-all:</strong> {plan.catchAll ?? "Not available"}</span>
        </li>
        <li className="offer-card__feature-heading">Email protection</li>
        {pricingProtectionFeatures.map((item) => (
          <li key={item}><CheckCircle2 size={18} />{item}</li>
        ))}
        <li className="offer-card__feature-heading">Business tools</li>
        {plan.features.slice(0, 6).map((item) => (
          <li key={item}><CheckCircle2 size={18} />{item}</li>
        ))}
      </ul>
    </article>
  );
}

const enterpriseFeatures = [
  "Email, calendar, tasks, contacts, chat, voice and video meetings and storage included",
  "Custom mailbox, domain and storage capacity",
  "Custom alias and distribution-list capacity",
  "Migration planning for complex environments",
  "Enterprise identity and access requirements",
  "Security, compliance and retention review",
  "Commercial terms aligned to your organization",
];

function EnterprisePlanCard() {
  return (
    <article className="offer-card offer-card--enterprise">
      <div className="offer-card__topline">
        <span>Enterprise</span>
        <span className="offer-card__descriptor">Custom</span>
      </div>
      <div className="offer-card__price offer-card__price--custom">
        <strong>Let's talk</strong>
        <span>requirements-based<br />annual agreement</span>
      </div>
      <p className="offer-card__lead">
        A tailored workspace for larger organizations, complex migrations and
        defined security or procurement requirements.
      </p>
      <p className="offer-card__protect">
        <ShieldCheck size={15} /> Envelaro Protect included
      </p>
      <div className="offer-card__actions">
        <ButtonLink href="/managed-private-deployment">
          Contact sales <ArrowRight size={18} />
        </ButtonLink>
      </div>
      <small className="offer-card__decision-note">
        Tell us about your users, domains, migration and control requirements. We will propose the right scope and commercial terms.
      </small>
      <div className="offer-card__metrics">
        <div><strong>100+</strong><span>mailboxes</span></div>
        <div><strong>25+</strong><span>custom domains</span></div>
        <div><strong>Custom</strong><span>storage and scope</span></div>
      </div>
      <ul className="offer-card__features">
        <li className="offer-card__feature-heading">Custom scope</li>
        {enterpriseFeatures.map((item) => (
          <li key={item}><CheckCircle2 size={18} />{item}</li>
        ))}
        <li className="offer-card__feature-heading">Email protection</li>
        {pricingProtectionFeatures.map((item) => (
          <li key={item}><CheckCircle2 size={18} />{item}</li>
        ))}
      </ul>
    </article>
  );
}

const envelaroProtectControls = [
  ["Sender authentication", "Guided SPF, DKIM and DMARC configuration."],
  ["Spam and phishing filtering", "Layered checks help stop suspicious messages before delivery."],
  ["Malware scanning", "Supported attachments are checked for known malicious content."],
  ["Connection security", "TLS protects supported mail-client and server connections in transit."],
  ["Account safeguards", "Login protection and sending controls help contain compromised accounts."],
  ["Abuse monitoring", "Unusual outbound behaviour is reviewed to protect platform reputation."],
];

function PlansPreview() {
  return (
    <section className="plans-preview">
      <div className="container">
        <div className="section-heading section-heading--center">
          <h2>Managed business email with predictable organization-level plans.</h2>
          <p>
            Start with the plan your business needs today and grow without
            paying for every new user.
          </p>
        </div>
        <article className="pricing-spotlight">
          <div className="pricing-spotlight__main">
            <div className="pricing-spotlight__identity">
              <span><Mail size={20} aria-hidden="true" /></span>
              <p>Business email plans</p>
            </div>
            <h3>Start small. Grow without a per-user bill.</h3>
            <p className="pricing-spotlight__copy">
              Give 10, 50 or 100 people email, calendar, tasks, contacts, chat,
              voice and video meetings and shared storage. Envelaro Protect is included.
            </p>
            <div className="pricing-spotlight__metrics">
              <div><strong>10, 50 or 100</strong><span>mailboxes</span></div>
              <div><strong>3, 10 or 25</strong><span>custom domains</span></div>
              <div><strong>10, 50 or 100 GB</strong><span>shared mail storage</span></div>
            </div>
            <ButtonLink href="/pricing" variant="light">
              Compare plans <ArrowRight size={17} />
            </ButtonLink>
          </div>
          <div className="pricing-spotlight__price">
            <span>Annual subscription</span>
            <strong><sup>$</sup>19</strong>
            <p>per organization<br />per month, billed annually</p>
            <div><ShieldCheck size={17} />Envelaro Protect included</div>
          </div>
        </article>
      </div>
    </section>
  );
}

const securityItems = [
  "TLS-protected connections",
  "SPF, DKIM and DMARC guidance",
  "Role-based administration",
  "Account activity visibility",
  "Backup and recovery planning",
  "Abuse prevention and monitoring",
];

function SecurityBand() {
  return (
    <section className="security-band">
      <div className="container security-band__grid">
        <div>
          <h2>Trust is part of the product.</h2>
          <p>
            Security, domain authentication and operational discipline belong
            in the foundation, not in an expensive add-on.
          </p>
          <ButtonLink href="/security">Explore security</ButtonLink>
        </div>
        <ul>
          {securityItems.map((item) => (
            <li key={item}>
              <Check size={18} />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="final-cta">
      <RoutingLine dark />
      <div className="container final-cta__inner">
        <h2>Make business email one less thing to operate.</h2>
        <p>
          Tell us about your domains, mailbox requirements and current provider.
          We will help you plan onboarding and migration.
        </p>
        <div className="button-row button-row--center">
          <ButtonLink href="/contact">Get started</ButtonLink>
          <ButtonLink href="/contact" variant="light">Talk to us</ButtonLink>
        </div>
      </div>
    </section>
  );
}

function PageHero({
  title,
  copy,
  children,
}: {
  title: ReactNode;
  copy: string;
  children?: ReactNode;
}) {
  return (
    <section className="page-hero">
      <div className="container page-hero__inner">
        <div>
          <h1>{title}</h1>
          <p>{copy}</p>
          {children}
        </div>
          <div className="page-hero__signal" aria-hidden="true">
            <span><Mail /></span><i /><span><CalendarDays /></span><i /><span><MessageCircle /></span><i /><span><FolderOpen /></span>
          </div>
      </div>
    </section>
  );
}

function ProductPage() {
  return (
    <main>
      <PageHero
        title="Managed business email hosting for the way your team actually works."
        copy="Run professional email on your own domain, then move from a message to a meeting, task, conversation, call or shared file without losing context or administrative control."
      >
        <ButtonLink href="/pricing">Explore plans</ButtonLink>
      </PageHero>
      <section className="product-detail product-detail--suite">
        <div className="container product-detail__grid">
          <ProductPreview />
          <div className="detail-list">
            {suiteFeatures.map(([Icon, title, copy]) => (
              <article key={title}>
                <Icon />
                <div><h2>{title}</h2><p>{copy}</p></div>
              </article>
            ))}
          </div>
        </div>
      </section>
      <ProductFlow />
      <SuiteBand />
      <ProductDeepDive />
      <MeetOffering />
      <ProductAdminBand />
      <AccessAnywhere />
      <Workflow />
      <RoleOutcomes />
      <SecurityBand />
      <RelatedMarketingLinks links={[
        ["Compare business email providers", "/resources/business-email-provider-comparison", "Use a practical checklist to compare migration, storage, support and the full cost of each option."],
        ["Manage several business domains", "/multiple-domain-email-hosting", "Plan email for related brands and domains owned by your organization."],
        ["Plan a provider migration", "/migration", "Review the access, data and delivery checks needed before moving your mail."],
      ]} />
      <FinalCta />
    </main>
  );
}

function ProductFlow() {
  const steps = [
    [Mail, "A message arrives", "Read and respond from focused business webmail."],
    [CalendarDays, "Time is agreed", "Turn the conversation into a shared meeting."],
    [SquareCheckBig, "Work is assigned", "Create a task with an owner and due date."],
    [MessageCircle, "The team aligns", "Continue in chat when a thread needs speed."],
    [FolderOpen, "The outcome is shared", "Keep the final file with the work that produced it."],
  ] as const;
  return (
    <section className="product-flow">
      <div className="container">
        <div className="product-flow__heading"><h2>From message to outcome, without losing the thread.</h2><p>Envelaro connects the ordinary transitions that make up a workday.</p></div>
        <div className="product-flow__rail">
          {steps.map(([Icon, title, copy], index) => (
            <article key={title}>
              <div><span>{String(index + 1).padStart(2, "0")}</span><Icon /></div>
              <h3>{title}</h3>
              <p>{copy}</p>
              {index < steps.length - 1 ? <ArrowRight aria-hidden="true" className="product-flow__arrow" /> : null}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function MeetOffering() {
  return <section className="meet-offering" id="envelaro-meet"><div className="container meet-offering__grid">
    <div><span className="meet-offering__eyebrow">ENVELARO MEET · INCLUDED IN PUBLIC PLANS</span><h2>From inbox to face-to-face.</h2><p>Good ideas deserve a conversation. Schedule a video meeting from your calendar, invite your team or guests, and come together in the browser.</p><ul><li>Calendar invitations with a meeting link</li><li>Video, voice and screen sharing</li><li>Guests wait until the host arrives</li></ul><div className="button-row"><ButtonLink href="https://meet.envelaro.com">Join a meeting <ArrowRight size={17}/></ButtonLink><ButtonLink href="/pricing" variant="secondary">Explore plans</ButtonLink></div></div>
    <div className="meet-offering__card"><Video size={40} aria-hidden="true"/><h3>Small meetings. Shared possibilities.</h3><dl><div><dt>1 host + 5 guests</dt><dd>Bring your people together</dd></div><div><dt>Up to 60 minutes</dt><dd>Per meeting</dd></div><div><dt>600 participant-minutes</dt><dd>Shared per organization, each month</dd></div></dl><p>Six people meeting for 30 minutes use 180 participant-minutes. The allowance is shared across your organization, not multiplied by mailbox count.</p></div>
  </div></section>;
}

function ProductDeepDive() {
  const groups = [
    {
      number: "01",
      title: "A professional front door for every conversation.",
      copy: "Give every person a mailbox on your business domain with webmail, standards-based client access and the identity controls a professional sender needs.",
      points: ["Business-domain webmail", "Secure IMAP and SMTP access", "Aliases and distribution lists", "SPF, DKIM and DMARC guidance"],
      icons: [Mail, Globe2, ShieldCheck],
    },
    {
      number: "02",
      title: "Plans become meetings, tasks and owned work.",
      copy: "Move decisions out of the inbox and into shared schedules and assigned tasks, where timing and responsibility remain visible.",
      points: ["Personal and shared calendars", "Meeting coordination", "Assigned tasks and due dates", "Visible ownership"],
      icons: [CalendarDays, SquareCheckBig, CheckCircle2],
    },
    {
      number: "03",
      title: "People can talk without changing their work identity.",
      copy: "Keep contacts, team chat, voice calls and video meetings inside the same managed organization instead of scattering business conversations across personal accounts.",
      points: ["Business contacts", "Direct and team chat", "Voice and video meetings", "Shared organization directory"],
      icons: [ContactRound, MessageCircle, PhoneCall],
    },
    {
      number: "04",
      title: "Files stay available after the conversation ends.",
      copy: "Store and share working files in the same workspace, with administrative visibility into the organization using them.",
      points: ["Shared file storage", "Plan-based capacity", "Central user administration", "Service activity visibility"],
      icons: [FolderOpen, DatabaseBackup, Settings2],
    },
  ] as const;
  return (
    <section className="product-deep-dive">
      <div className="container">
        <div className="product-deep-dive__intro"><h2>The complete product, layer by layer.</h2><p>Each capability is useful alone. Together, they reduce the number of places where work and accountability disappear.</p></div>
        <div className="product-deep-dive__rows">
          {groups.map((group, index) => (
            <article key={group.number} className={index % 2 ? "is-reversed" : ""}>
              <div className="product-deep-dive__copy"><span>{group.number}</span><h3>{group.title}</h3><p>{group.copy}</p><ul>{group.points.map((point) => <li key={point}><CheckCircle2 />{point}</li>)}</ul></div>
              <div className="product-deep-dive__visual" aria-hidden="true">
                {group.icons.map((Icon, iconIndex) => <span key={iconIndex}><Icon /></span>)}
                <i /><i />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductAdminBand() {
  const controls = [
    [Globe2, "Domains", "Verify ownership and follow mail-routing readiness."],
    [Users, "People", "Create mailboxes, aliases, groups and access."],
    [ShieldCheck, "Protection", "Guide authentication and responsible sending policy."],
    [Radar, "Visibility", "Review service state and important administrative activity."],
  ] as const;
  return (
    <section className="product-admin-band">
      <div className="container product-admin-band__grid">
        <div><h2>A calm control plane behind a busy workday.</h2><p>Team members see the tools they use. Administrators see the domains, identities, policies and service state they are responsible for.</p><ButtonLink href="/security" variant="light">Explore administration and security <ArrowRight size={17} /></ButtonLink></div>
        <div className="product-admin-band__controls">{controls.map(([Icon, title, copy]) => <article key={title}><Icon /><div><h3>{title}</h3><p>{copy}</p></div></article>)}</div>
      </div>
    </section>
  );
}

function PricingPage() {
  return (
    <main>
      <PageHero
        title={
          <>
            <span className="pricing-title-line">
              Start with <strong>10</strong>.
            </span>
            <span className="pricing-title-line">
              Grow to <strong>100</strong>.
            </span>
          </>
        }
        copy="Start with a card-free 7-business-day trial, or choose the annual plan that fits your team. Every plan includes verified business domains, Business Suite and Envelaro Protect."
      >
        <div className="button-row page-hero__actions">
          <ButtonLink href={TRIAL_SIGNUP_URL}>Start free trial</ButtonLink>
          <ButtonLink external href="#offer" variant="secondary">Compare plans</ButtonLink>
        </div>
      </PageHero>
      <section className="offer-section" id="offer">
        <div className="container">
          <div className="offer-layout">
            {pricingPlans.map((plan) => <PricingPlanCard key={plan.name} plan={plan} />)}
            <EnterprisePlanCard />
          </div>
          <p className="pricing-note">Managing several brands? <Link href="/multiple-domain-email-hosting">See how multiple-domain email hosting works</Link> within your organization's plan limits. Comparing providers? <Link href="/resources/business-email-provider-comparison">Use the business email comparison checklist</Link> before choosing.</p>
          <div className="offer-support-grid">
            <article className="storage-card">
              <span className="eyebrow">Need more room?</span>
              <DatabaseBackup size={30} />
              <h2>Mail Storage Pack</h2>
              <p>Add storage to any plan without changing mailbox or domain capacity.</p>
              <div className="storage-card__price">
                <strong>$69</strong>
                <span>per 25 GB / year</span>
              </div>
              <ul>
                {pricingPlans.map((plan) => (
                  <li key={plan.id}>
                    <span>{plan.name}</span>
                    <strong>{plan.storageGb} GB  /  ${plan.priceAnnual}</strong>
                  </li>
                ))}
                <li><span>Each Mail Storage Pack</span><strong>+25 GB  /  $69</strong></li>
              </ul>
            </article>
            <article className="roadmap-card">
              <span>Email client access</span>
              <h3>Use the applications you already know.</h3>
              <p className="roadmap-card__intro">
                All plans include Business Suite.
              </p>
              <ul className="roadmap-card__apps" aria-label="Business Suite applications">
                {[
                  "Webmail",
                  "Team chat",
                  "Voice and video meetings",
                  "Contacts",
                  "Calendar",
                  "Shared storage",
                  "Tasks",
                ].map((app) => (
                  <li key={app}>
                    <CheckCircle2 aria-hidden="true" size={16} />
                    <span>{app}</span>
                  </li>
                ))}
              </ul>
            </article>
          </div>
        </div>
      </section>
      <PricingComparison />
      <section className="mail-guard-section" id="mail-guard">
        <div className="container">
          <div className="section-heading section-heading--wide">
            <div>
              <span className="eyebrow">Envelaro Protect</span>
              <h2>Protection is included, not upsold.</h2>
            </div>
            <p>
              Every subscription includes a layered security baseline for
              domain identity, incoming mail, account access and platform
              reputation.
            </p>
          </div>
          <div className="mail-guard-grid">
            {envelaroProtectControls.map(([title, copy]) => (
              <article key={title}>
                <ShieldCheck size={22} />
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="usage-section">
        <div className="container usage-section__grid">
          <div>
            <span className="eyebrow">Clear boundaries</span>
            <h2>Generous address space. Responsible business use.</h2>
          </div>
          <div>
            {[
              "All mailboxes share the organization's purchased storage.",
              "Every domain must be verified through a unique DNS record.",
              "Published sending limits apply separately to every mailbox.",
              "Bulk marketing, spam and unauthorized third-party resale are excluded.",
            ].map((item) => <p key={item}><CheckCircle2 />{item}</p>)}
          </div>
        </div>
      </section>
      <section className="usage-section">
        <div className="container usage-section__grid">
          <div>
            <span className="eyebrow">Before you pay</span>
            <h2>Billing terms you can review in advance.</h2>
          </div>
          <div>
            <p><CheckCircle2 />The seven-business-day trial requires no card and never converts automatically.</p>
            <p><CheckCircle2 />Paid plans are billed annually in advance; currency, tax, total, renewal timing, and the payment provider are shown at checkout.</p>
            <p><CheckCircle2 />Cancel renewal at any time. Initial self-service purchases have a 14-calendar-day refund window.</p>
            <p><CheckCircle2 /><Link href="/billing-policy">Read billing, cancellation, and refund details</Link>.</p>
          </div>
        </div>
      </section>
      <PricingQuestions />
      <FinalCta />
    </main>
  );
}

function PricingComparison() {
  return (
    <section className="pricing-table-section" aria-labelledby="pricing-comparison-title">
      <div className="container">
        <span className="eyebrow">Compare business email plans</span>
        <h2 id="pricing-comparison-title">See the limits that change from plan to plan.</h2>
        <p className="pricing-comparison-intro">
          Business Suite and Envelaro Protect are included across the public plans. Compare organization capacity here, then review the plan cards for complete features and purchase terms.
        </p>
        <p className="pricing-table-hint">Scroll horizontally to compare all plans.</p>
        <div className="pricing-table-wrap" tabIndex={0} aria-label="Scrollable business email plan comparison">
          <table className="pricing-table">
            <caption>Envelaro business email plan capacity comparison</caption>
            <thead>
              <tr>
                <th scope="col">Plan capacity</th>
                {pricingPlans.map((plan) => (
                  <th className={plan.featured ? "pricing-table__featured" : undefined} key={plan.id} scope="col">
                    {plan.featured ? <span>Best value</span> : null}
                    <strong>{plan.name}</strong>
                    <div><b>${plan.priceAnnual / 12}</b><small>/month</small></div>
                    <em>Billed ${plan.priceAnnual.toLocaleString("en-US")} annually</em>
                  </th>
                ))}
                <th scope="col"><strong>Enterprise</strong><div><b>Custom</b></div><em>Requirements-based agreement</em></th>
              </tr>
            </thead>
            <tbody>
              {pricingComparisonRows.map(([label, ...values]) => (
                <tr key={label}>
                  <th scope="row">{label}</th>
                  {values.map((value, index) => <td key={`${label}-${index}`}>{value}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="pricing-note">Aliases and distribution lists use the published address capacity shown in each plan card. Sending and abuse safeguards always apply.</p>
      </div>
    </section>
  );
}

function PricingQuestions() {
  return (
    <section className="home-faq pricing-faq" aria-labelledby="pricing-faq-title">
      <div className="container home-faq__grid">
        <div>
          <span className="eyebrow">Buying business email</span>
          <h2 id="pricing-faq-title">Questions to settle before choosing a plan.</h2>
          <p>Review the complete product, migration, administration and security answers in the Envelaro FAQ.</p>
          <ButtonLink href="/faq" variant="secondary">Read all FAQs <ArrowRight size={17} /></ButtonLink>
        </div>
        <div>
          {pricingQuestions.map(([question, answer]) => (
            <details key={question}>
              <summary>{question}<span aria-hidden="true">+</span></summary>
              <p>{answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function SecurityPage() {
  const controls = [
    [LockKeyhole, "Connection security", "TLS protects supported browser, mail-client and server connections in transit."],
    [KeyRound, "Domain authentication", "Guided SPF, DKIM and DMARC setup helps protect your domain identity."],
    [ShieldCheck, "Account protection", "Administrative access is designed around verified identity and least privilege."],
    [DatabaseBackup, "Recovery planning", "Backup and recovery are treated as operating disciplines, not afterthoughts."],
    [Radar, "Abuse prevention", "Sending controls and monitoring protect customers and platform reputation."],
    [ServerCog, "Operational boundaries", "Customer, administrative and mail-service surfaces are deliberately separated."],
  ] as const;
  return (
    <main>
      <PageHero
        title="Trust requires evidence, not a wall of badges."
        copy="Envelaro combines practical email security, accountable administration and clear documentation, while publishing certification claims only after independent assessment."
      />
      <section className="trust-ledger">
        <div className="container trust-ledger__grid">
          <div>
            <h2>A transparent compliance posture.</h2>
            <p>Security controls exist today. Formal certifications are a separate milestone and will never be implied before the relevant audit is complete.</p>
          </div>
          <div className="trust-ledger__items">
            <article><span className="trust-status trust-status--active">Available now</span><h3>Operational controls</h3><p>TLS, domain authentication guidance, role-based administration, activity records, responsible sending controls and published legal documents.</p></article>
            <article><span className="trust-status trust-status--documented">Documented</span><h3>Privacy and service transparency</h3><p>Privacy terms, DPA, subprocessors, acceptable-use rules and service-availability information are maintained in the Trust Center.</p></article>
            <article><span className="trust-status trust-status--roadmap">Compliance roadmap</span><h3>SOC 2 and ISO 27001</h3><p>Not currently certified. Readiness and independent assessment will be communicated with scope and evidence when formally scheduled.</p></article>
          </div>
        </div>
      </section>
      <section className="control-grid">
        <div className="container">
          <div className="section-heading section-heading--wide"><div><h2>Controls designed for accountable operations.</h2></div><p>Practical safeguards for identity, access, transport, recovery and platform reputation.</p></div>
          <div className="control-grid__items">
            {controls.map(([Icon, title, copy]) => (
              <article key={title}>
                <Icon />
                <h2>{title}</h2>
                <p>{copy}</p>
              </article>
            ))}
          </div>
          <p className="security-note">Security and compliance requirements vary by organization. Contact us for a scope-specific review before relying on Envelaro for a regulated workload.</p>
        </div>
      </section>
      <SecurityBand />
      <FinalCta />
    </main>
  );
}

function MigrationSupportForm() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [startedAt] = useState(() => Date.now());

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!launchListEnabled) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    setSubmitting(true);
    setError("");
    try {
      await submitLaunchLead({
        name: String(data.get("name") ?? ""),
        email: String(data.get("email") ?? ""),
        company: String(data.get("company") ?? ""),
        teamSize: String(data.get("mailboxCount") ?? ""),
        currentProvider: String(data.get("currentProvider") ?? ""),
        message: `Move existing email request. Largest mailbox: ${String(data.get("mailboxSize") ?? "Not specified")}. ${String(data.get("message") ?? "")}`,
        consent: true,
        privacyVersion: "website-privacy-2026-08-26",
        source: "website_contact",
        website: String(data.get("website") ?? ""),
        elapsedMs: Date.now() - startedAt,
      });
      setSubmitted(true);
      form.reset();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "We could not save your migration request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="contact-form migration-support__form" onSubmit={handleSubmit}>
      <label>Name<input autoComplete="name" name="name" required minLength={2} /></label>
      <label>Work email<input autoComplete="email" name="email" required type="email" /></label>
      <label>Company<input autoComplete="organization" name="company" maxLength={140} /></label>
      <label>Number of mailboxes<input inputMode="numeric" min="1" name="mailboxCount" placeholder="For example, 25" required type="number" /></label>
      <label>Current email provider<input name="currentProvider" maxLength={120} placeholder="Google Workspace, Microsoft 365, cPanel..." required /></label>
      <label>Largest mailbox size<input name="mailboxSize" maxLength={80} placeholder="For example, 2.4 GB" required /></label>
      <label className="contact-form__wide">What should we know about the move?<textarea name="message" minLength={10} maxLength={2700} rows={4} placeholder="Domains, deadline, special folders, shared mailboxes, or access constraints" required /></label>
      <label className="contact-form__honeypot" aria-hidden="true">Website<input name="website" tabIndex={-1} autoComplete="off" /></label>
      <label className="contact-consent"><input name="consent" type="checkbox" required /><span>I agree that Envelaro may store these details and contact me about this migration. See our <Link href="/privacy">privacy notice</Link>.</span></label>
      <button className="button button--primary" type="submit" disabled={!launchListEnabled || submitting || submitted}>{submitted ? "Request sent" : submitting ? "Sending..." : "Move existing email"}{submitted ? <CheckCircle2 size={18} /> : <ArrowRight size={18} />}</button>
      {!launchListEnabled ? <p className="form-note form-note--pending">Online requests are temporarily unavailable. Please try again later.</p> : null}
      {submitted ? <p className="form-note">Thanks. We received your migration request and will be in touch.</p> : null}
      {error ? <p className="form-note form-note--error" role="alert">{error}</p> : null}
    </form>
  );
}

function MigrationPage() {
  const phases = [
    ["01", "Assess the source", "Inventory domains, mailboxes, aliases, groups, forwarding rules and data volume. We confirm the available access method before setting a date."],
    ["02", "Build the destination", "Create the Envelaro tenant, verify domains and provision mailboxes before changing live mail routing."],
    ["03", "Run the initial copy", "Copy eligible historical email while the existing provider continues receiving new messages."],
    ["04", "Validate before cutover", "Check mailbox counts, folders, permissions, authentication records and user access. Exceptions are resolved before MX changes."],
    ["05", "Switch mail routing", "Update MX and related DNS records in a controlled window, then confirm inbound and outbound delivery."],
    ["06", "Delta sync and closeout", "Capture eligible messages that arrived during the transition, monitor delivery and keep the previous service available until sign-off."],
  ];
  const sourceTypes = [
    ["Google Workspace", "OAuth or administrator-authorized access, subject to source configuration."],
    ["Microsoft 365", "Administrator-authorized migration using supported Microsoft access methods."],
    ["Hosted IMAP", "Mailbox credentials or administrator-provided access for compatible IMAP servers."],
    ["Control-panel email", "Assessment for cPanel, Plesk and other hosting environments with accessible mailboxes."],
    ["Other providers", "We validate protocol support, authentication and export options before committing to a method."],
    ["Manual import", "A fallback path for supported mailbox exports when direct source access is unavailable."],
  ];
  return (
    <main>
      <PageHero
        title="Move business email without gambling on the cutover."
        copy="Envelaro migration services plan the move around your source system, mailbox volume and acceptable transition window. Historical email is prepared first; live routing changes only after the destination is ready."
      >
        <ButtonLink href="/migration#move-existing-email">Move existing email</ButtonLink>
      </PageHero>

      <section className="migration-free-offer">
        <div className="container migration-free-offer__grid">
          <div className="migration-free-offer__copy">
            <span className="migration-kicker">Included self-service migration</span>
            <h2>Bring your recent email with you—free.</h2>
            <p>Each eligible mailbox can copy up to <strong>500 email messages</strong> and <strong>500 MB</strong> from a compatible IMAP provider after the user signs in to Envelaro.</p>
          </div>
          <div className="migration-free-offer__limits" aria-label="Free migration limits">
            <article><strong>500</strong><span>email messages</span></article>
            <article><strong>500 MB</strong><span>mailbox data</span></article>
          </div>
        </div>
      </section>

      <section className="migration-support" id="move-existing-email">
        <div className="container migration-support__grid">
          <div><span className="migration-kicker">Need a larger move?</span><h2>Is your old mailbox more than 500 MB?</h2><p>Or does it contain more than 500 messages? We are here to support the move. Tell us about the current provider and mailbox size, and our team will help plan the right migration path.</p></div>
          <MigrationSupportForm />
        </div>
      </section>

      <section className="migration-overview">
        <div className="container migration-overview__grid">
          <div>
            <span className="migration-kicker">A controlled transition</span>
            <h2>Your old provider stays available while the new environment is prepared.</h2>
            <p>Mailbox creation and the first eligible data copy happen before the final DNS cutover. That separates the time-consuming transfer from the short routing change and gives your administrator a clear validation point.</p>
          </div>
          <div className="migration-route" aria-label="Migration routing sequence">
            <div><span>1</span><strong>Existing provider</strong><small>Continues receiving mail</small></div>
            <i aria-hidden="true" />
            <div><span>2</span><strong>Initial copy</strong><small>Historical email moves first</small></div>
            <i aria-hidden="true" />
            <div><span>3</span><strong>DNS cutover</strong><small>New mail routes to Envelaro</small></div>
          </div>
        </div>
      </section>

      <section className="migration-sources">
        <div className="container">
          <div className="section-heading section-heading--wide">
            <div><span className="migration-kicker">Source assessment</span><h2>Start with the system you have today.</h2></div>
            <p>Access and automation vary by provider. We confirm the supported method before promising a schedule.</p>
          </div>
          <div className="migration-sources__grid">
            {sourceTypes.map(([title, copy], index) => (
              <article key={title}><span>0{index + 1}</span><h3>{title}</h3><p>{copy}</p></article>
            ))}
          </div>
          <nav className="migration-provider-links" aria-label="Provider migration guides">
            <strong>Review a provider-specific guide:</strong>
            <Link href="/migration/cpanel">cPanel</Link>
            <Link href="/migration/godaddy">GoDaddy</Link>
            <Link href="/migration/google-workspace">Google Workspace</Link>
            <Link href="/migration/microsoft-365">Microsoft 365</Link>
            <Link href="/migration/zoho-mail">Zoho Mail</Link>
          </nav>
        </div>
      </section>

      <section className="migration-authorization">
        <div className="container migration-authorization__grid">
          <div className="migration-authorization__intro">
            <span className="migration-kicker">Clear responsibility</span>
            <h2>Who authorizes the mailbox copy?</h2>
            <p>The answer depends on the source provider. Envelaro does not ask an administrator to collect user passwords when an organization-wide authorization method is available.</p>
          </div>
          <article>
            <span>Preferred</span>
            <h3>Administrator-authorized</h3>
            <p>An authorized tenant administrator grants scoped access for supported business platforms. This is the cleanest path for bulk mailbox processing and centralized oversight.</p>
          </article>
          <article>
            <span>When required</span>
            <h3>User-authorized</h3>
            <p>Each mailbox owner may need to approve OAuth access or provide source credentials when the provider does not offer suitable organization-wide delegation.</p>
          </article>
        </div>
      </section>

      <section className="migration-page">
        <div className="container migration-page__layout">
          <div className="migration-page__heading">
            <span className="migration-kicker">The migration runbook</span>
            <h2>Six checkpoints from discovery to sign-off.</h2>
            <p>Every phase has an owner and an exit condition. DNS is not the first step.</p>
          </div>
          <div className="migration-page__steps">
            {phases.map(([number, title, copy]) => (
              <article key={number}>
                <span>{number}</span><div><h3>{title}</h3><p>{copy}</p></div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="migration-scope">
        <div className="container">
          <div className="migration-scope__header">
            <span className="migration-kicker">Define success before transfer</span>
            <h2>Know what is included, validated and handled separately.</h2>
          </div>
          <div className="migration-scope__grid">
            <article><span>01</span><h3>Mailbox data</h3><p>Eligible messages and folders are copied using the selected migration method. Flags, dates and folder structure are validated where the source protocol exposes them.</p></article>
            <article><span>02</span><h3>Addresses and routing</h3><p>Mailboxes, aliases, groups and forwarding requirements are inventoried and recreated as agreed. DNS changes are staged rather than silently overwriting unrelated records.</p></article>
            <article><span>03</span><h3>Calendars and contacts</h3><p>These are assessed separately because portability depends on the source platform, export format and supported APIs. They are not implied by an email-only migration.</p></article>
            <article><span>04</span><h3>Client readiness</h3><p>Users receive access details for Envelaro webmail and supported mail clients. Device reconfiguration and organization-specific rollout support are scoped during planning.</p></article>
          </div>
          <div className="migration-scope__note"><strong>No hidden cutover.</strong><p>Your administrator approves the routing change after destination mailboxes and required DNS records have been reviewed.</p></div>
        </div>
      </section>

      <section className="migration-assessment">
        <div className="container migration-assessment__grid">
          <div><span className="migration-kicker">Before we set a date</span><h2>Bring us five facts. We will turn them into a migration plan.</h2></div>
          <ul>
            <li><span>01</span>Current email provider and administrator access model</li>
            <li><span>02</span>Number of domains, mailboxes, aliases and groups</li>
            <li><span>03</span>Estimated mailbox data volume and largest mailbox</li>
            <li><span>04</span>Required calendars, contacts, archives or shared mailboxes</li>
            <li><span>05</span>Preferred cutover window and acceptable coexistence period</li>
          </ul>
          <div className="migration-assessment__action"><p>We will confirm the viable method, responsibilities, constraints and next checkpoint before migration begins.</p><ButtonLink href="/contact">Discuss your current setup</ButtonLink></div>
        </div>
      </section>
      <MigrationBand />
      <FinalCta />
    </main>
  );
}

function ResourcesPage() {
  const questions = [
    ["Can I use my existing domain?", "Yes. Envelaro is designed for custom-domain business email. You will keep ownership of your domain."],
    ["Can you help with migration?", "Yes. Migration options depend on your current provider and mailbox format. Contact us with your current setup and we will help plan the move."],
    ["Where is Envelaro available?", "Envelaro serves approved business customers in supported regions. Contact us to confirm service availability for your organization."],
    ["How are the business email plans priced?", "Starter is $228 per organization per year with up to 10 mailboxes, 3 verified custom domains and 10 GB of shared mail storage. Growth is $948 per year with up to 50 mailboxes, 10 verified custom domains and 50 GB of shared mail storage. Scale is $1,668 per year with up to 100 mailboxes, 25 verified custom domains and 100 GB of shared mail storage. Additional 25 GB Mail Storage Packs are $69 per year."],
    ["Can I become a reseller?", "Envelaro business email plans are for organizations managing their own domains. Third-party resale is not included."],
  ];
  return (
    <main>
      <PageHero
        title="Clear answers before you move your email."
        copy="Practical business email guides and straight answers about domains, pricing, migration, service boundaries and support."
      />
      <section className="resource-paths">
        <div className="container">
          <div className="section-heading section-heading--wide">
            <div><span className="eyebrow">Business email guides</span><h2>Start with the decision you are making.</h2></div>
            <p>Explore focused guidance for choosing a service model, preparing domains and moving from a current provider.</p>
          </div>
          <div className="resource-paths__grid">
            {[
              ["Email hosting for small business", "/small-business-email-hosting", "Compare the operational middle ground between basic hosted mailboxes and per-seat enterprise suites."],
              ["Custom-domain email hosting", "/custom-domain-email-hosting", "Understand ownership, verification, mail routing and authentication on a domain you control."],
              ["Multiple-domain email hosting", "/multiple-domain-email-hosting", "Plan mailboxes and authentication across several owned business domains."],
              ["Managed email setup", "/managed-email-setup", "Follow the path from domain review and mailbox mapping to tested mail flow."],
            ].map(([title, href, copy]) => <Link href={href} key={href}><strong>{title}</strong><span>{copy}</span><ArrowRight aria-hidden="true" /></Link>)}
          </div>
        </div>
      </section>
      <section className="resource-paths resource-paths--migration">
        <div className="container">
          <div className="section-heading section-heading--wide">
            <div><span className="eyebrow">Provider migration guides</span><h2>Prepare for the source system you have today.</h2></div>
            <p>Each guide identifies the access, DNS and non-email dependencies that need a decision before cutover.</p>
          </div>
          <div className="resource-paths__grid resource-paths__grid--five">
            {[
              ["cPanel", "/migration/cpanel"],
              ["GoDaddy", "/migration/godaddy"],
              ["Google Workspace", "/migration/google-workspace"],
              ["Microsoft 365", "/migration/microsoft-365"],
              ["Zoho Mail", "/migration/zoho-mail"],
            ].map(([provider, href]) => <Link href={href} key={href}><strong>Migrate from {provider}</strong><span>Review the source-specific migration checklist.</span><ArrowRight aria-hidden="true" /></Link>)}
          </div>
        </div>
      </section>
      <section className="resource-paths resource-paths--evidence">
        <div className="container">
          <div className="section-heading section-heading--wide">
            <div><span className="eyebrow">Evidence-backed explainers</span><h2>Use primary sources to understand the control, not just the acronym.</h2></div>
            <p>These guides connect practical business-email decisions to standards and official provider documentation.</p>
          </div>
          <div className="resource-paths__grid">
            {RESOURCE_GUIDES.map((guide) => (
              <Link href={guide.path} key={guide.path}>
                <strong>{guide.title}</strong>
                <span>{guide.description}</span>
                <ArrowRight aria-hidden="true" />
              </Link>
            ))}
          </div>
        </div>
      </section>
      <section className="faq-section">
        <div className="container faq-section__grid">
          <h2>Frequently asked questions</h2>
          <div>
            {questions.map(([question, answer]) => (
              <details key={question}>
                <summary>{question}<span>+</span></summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
      <FinalCta />
    </main>
  );
}

function ResourceGuidePage({ guide }: { guide: ResourceGuide }) {
  return (
    <main>
      <PageHero title={guide.title} copy={guide.description}>
        <ButtonLink href="/resources" variant="secondary">Browse all resources</ButtonLink>
      </PageHero>
      <article className="resource-guide">
        <div className="container resource-guide__layout">
          <aside className="resource-guide__toc">
            <span className="migration-kicker">{guide.eyebrow}</span>
            <p>Published by Envelaro on <time dateTime={guide.publishedAt ?? "2026-08-28"}>{new Date(`${guide.publishedAt ?? "2026-08-28"}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" })}</time>.</p>
            {guide.updatedAt && guide.updatedAt !== (guide.publishedAt ?? "2026-08-28") ? <p>Updated <time dateTime={guide.updatedAt}>{new Date(`${guide.updatedAt}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" })}</time>.</p> : null}
            <nav aria-label="Guide sections">
              {guide.sections.map((section) => <a href={`#${section.id}`} key={section.id}>{section.title}</a>)}
              <a href="#sources">Primary sources</a>
            </nav>
          </aside>
          <div className="resource-guide__content">
            <section className="resource-guide__introduction">
              {guide.introduction.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            </section>
            {guide.sections.map((section) => (
              <section id={section.id} key={section.id}>
                <h2>{section.title}</h2>
                {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                {section.bullets ? <ul>{section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul> : null}
                {section.table ? <div className="resource-guide__table-scroll" role="region" aria-label={section.table.caption} tabIndex={0}>
                  <table>
                    <caption>{section.table.caption}</caption>
                    <thead><tr>{section.table.columns.map((column) => <th scope="col" key={column}>{column}</th>)}</tr></thead>
                    <tbody>{section.table.rows.map(([label, ...cells]) => <tr key={label}><th scope="row">{label}</th>{cells.map((cell, index) => <td key={index}>{cell}</td>)}</tr>)}</tbody>
                  </table>
                </div> : null}
              </section>
            ))}
            <section id="sources" className="resource-guide__sources">
              <h2>Primary sources</h2>
              <p>Provider interfaces and requirements change. Check the linked source before applying a production change.</p>
              <ol>
                {guide.sources.map((source) => (
                  <li key={source.url}><a href={source.url} rel="noreferrer" target="_blank">{source.label}</a></li>
                ))}
              </ol>
            </section>
          </div>
        </div>
      </article>
      <RelatedMarketingLinks links={guide.related} />
      <FinalCta />
    </main>
  );
}

type SolutionLandingContent = {
  eyebrow: string;
  title: string;
  description: string;
  promise: string;
  overview: string[];
  benefits: readonly (readonly [string, string])[];
  fit: readonly string[];
  process: readonly (readonly [string, string])[];
  related: readonly (readonly [string, string, string])[];
};

const solutionLandingContent = {
  smallBusiness: {
    eyebrow: "Email hosting for growing teams",
    title: "Business email hosting for small businesses that need real support.",
    description: "Use professional email on your own domain without turning mailbox setup, DNS and migration into another job for the owner or office manager.",
    promise: "Envelaro combines custom-domain mailboxes, a connected business workspace and guided administration in an organization-level plan.",
    overview: [
      "A small business should not have to choose between an unsupported mailbox bundled with web hosting and a complex enterprise suite priced one person at a time. Envelaro provides a managed middle path: professional addresses on the domain you own, practical security controls and a clear place to administer the team.",
      "The service is designed around the work that surrounds a mailbox. We help prepare the domain, create users and aliases, validate mail routing and guide SPF, DKIM and DMARC configuration. If email already exists elsewhere, the source and data volume are assessed before any cutover date is proposed.",
      "Team members can work through Envelaro webmail and supported standards-based mail clients. Administrators retain responsibility for approving domains, users and routing changes, while Envelaro makes those responsibilities visible instead of hiding them behind a one-click promise.",
    ],
    benefits: [
      ["Professional from the first message", "Create recognizable addresses such as name@company.com, sales@company.com and accounts@company.com on domains your organization controls."],
      ["Capacity without a seat-by-seat puzzle", "Choose an organization plan with defined mailbox, domain and shared-storage capacity, then manage the included allocation as the team changes."],
      ["Setup with an accountable path", "Follow guided domain verification, mailbox creation, authentication and delivery checks, with migration planning when an existing service must be preserved."],
    ],
    fit: [
      "Small service companies moving away from personal Gmail or Outlook addresses",
      "Growing teams that need several professional mailboxes and role addresses",
      "Organizations replacing email bundled with a website or control panel",
      "Administrators who want email, calendars, contacts, tasks and communication in one workspace",
    ],
    process: [
      ["Choose capacity", "Compare the included mailboxes, domains and shared mail storage rather than estimating a separate licence for every future address."],
      ["Verify the domain", "Confirm domain ownership and review the exact DNS records required for mail routing and sender authentication."],
      ["Create the team", "Provision named mailboxes, aliases and groups according to the approved organization structure."],
      ["Validate before use", "Test access and mail flow, then complete any planned historical-email migration and source-service closeout."],
    ],
    related: [
      ["Compare organization plans", "/pricing", "Review included mailboxes, domains and shared storage."],
      ["Move existing email", "/migration", "Plan a controlled migration from the provider you use today."],
      ["Check your email domain", "/tools/email-health", "Review public MX, SPF, DKIM and DMARC evidence."],
    ],
  },
  customDomain: {
    eyebrow: "Email on a domain you own",
    title: "Custom-domain email hosting, configured for business use.",
    description: "Give your team consistent company email addresses while keeping ownership of your domain, brand and DNS decisions.",
    promise: "Envelaro hosts professional business mailboxes and guides the domain records that connect your identity to dependable mail flow.",
    overview: [
      "Custom-domain email replaces a generic address such as company@gmail.com with an address on your own domain. It creates a consistent business identity for employees and role accounts, but the visible address is only one part of a reliable setup. Mail routing, sender authentication, access and administration must also be configured correctly.",
      "Your organization continues to own and control its domain. Envelaro provides the verification, MX and authentication requirements needed for the service, while an authorized administrator approves publication through the domain's DNS provider. Website and unrelated DNS records are reviewed separately and should not be overwritten during email setup.",
      "Once the domain is ready, administrators can create mailboxes and approved aliases, provide access to users and review service state from one place. SPF, DKIM and DMARC guidance helps receiving services verify legitimate messages and makes impersonation policy more explicit.",
    ],
    benefits: [
      ["Keep control of your name", "Use the domain your customers already recognize. Moving email providers later does not require changing every public-facing address."],
      ["Separate people from roles", "Create named users alongside approved addresses such as billing@, support@ or enquiries@ without mixing company communication into personal accounts."],
      ["Authenticate the sending domain", "Publish and validate the required SPF, DKIM and DMARC records instead of assuming an MX change alone completes the setup."],
    ],
    fit: [
      "New businesses creating professional email for an existing domain",
      "Organizations replacing generic personal addresses with a consistent company identity",
      "Teams separating their website host from their email provider",
      "Businesses managing more than one verified brand or operating domain",
    ],
    process: [
      ["Confirm ownership", "Add the requested verification record without transferring ownership of the domain to Envelaro."],
      ["Map addresses", "List the mailboxes, aliases, groups and catch-all requirements the organization actually needs."],
      ["Prepare authentication", "Review MX, SPF, DKIM and DMARC values and preserve website, application and third-party sending records."],
      ["Activate and test", "Confirm inbound delivery, outbound authentication and user access before retiring any previous mailbox service."],
    ],
    related: [
      ["Review email security", "/security", "Understand authentication, access and administrative controls."],
      ["Host multiple domains", "/multiple-domain-email-hosting", "Manage several verified domains within one organization plan."],
      ["Check domain readiness", "/tools/email-health", "Inspect the public records visible to receiving mail systems."],
    ],
  },
  multipleDomain: {
    eyebrow: "One organization, several domains",
    title: "Multiple-domain email hosting without fragmented administration.",
    description: "Manage verified domains, mailboxes and shared capacity together while keeping each business identity clear.",
    promise: "Envelaro organization plans include defined multi-domain capacity so growing companies can administer related brands from one operational view.",
    overview: [
      "Businesses often operate more than one domain after launching a new brand, acquiring a company, serving a regional market or protecting common name variations. Buying and administering an isolated email subscription for each domain can scatter users, DNS work and renewal decisions across several systems.",
      "Envelaro lets an authorized administrator add verified domains within the selected plan's capacity. Mailboxes and aliases remain deliberate: addresses are assigned to the appropriate people or roles, and each domain completes its own ownership, routing and authentication checks before it is used for sending.",
      "Shared plan storage provides flexibility across the organization, but capacity is not unlimited. Current plans publish the maximum domains, mailboxes and mail storage included. Organizations that require client resale, strict tenant separation or capacity beyond those boundaries need a separate commercial and deployment assessment.",
      "This structure is intended for domains owned and operated by the same customer organization. Agencies or service providers managing unrelated client domains need explicit commercial approval and stronger tenant boundaries rather than placing every customer inside one shared organization.",
    ],
    benefits: [
      ["A single administrative picture", "Review related domains and mailbox capacity in one organization instead of switching between unrelated hosting accounts."],
      ["A clear identity for every brand", "Create addresses on the right domain while preserving separate SPF, DKIM, DMARC and routing evidence for each identity."],
      ["Predictable published limits", "Choose a plan by organization capacity and add mail storage when needed, without presenting every alias as a separate paid seat."],
    ],
    fit: [
      "Companies operating several owned brands or regional domains",
      "Groups consolidating domains after a merger or acquisition",
      "Organizations moving mail away from several website-hosting accounts",
      "Teams that need a mix of named mailboxes and role-based addresses",
    ],
    process: [
      ["Inventory every domain", "Identify ownership, current providers, active mailboxes, aliases, applications and sending services before consolidation."],
      ["Choose the organization plan", "Match the required domains, mailboxes and shared storage to published capacity or request a private deployment review."],
      ["Verify domains separately", "Complete ownership and authentication for each domain without assuming one successful setup covers the others."],
      ["Migrate in controlled groups", "Sequence mailbox copies and DNS cutovers so one brand's transition does not create unnecessary risk for another."],
    ],
    related: [
      ["See plan capacities", "/pricing", "Compare included domains, mailboxes and shared storage."],
      ["Plan provider migrations", "/migration", "Assess each source system before scheduling DNS changes."],
      ["Request private deployment", "/managed-private-deployment", "Discuss isolation, regional or higher-capacity requirements."],
    ],
  },
  managedSetup: {
    eyebrow: "Guided onboarding and configuration",
    title: "Managed business email setup from domain review to tested mail flow.",
    description: "Turn a domain and a mailbox list into an operational email environment with clear responsibilities and validation checkpoints.",
    promise: "Envelaro helps administrators prepare domains, users, DNS authentication and access instead of leaving the organization with an undocumented handoff.",
    overview: [
      "Buying email service does not by itself create a reliable operating environment. Someone still has to verify the domain, map users and role addresses, publish DNS records, preserve third-party senders, configure access and prove that messages can travel in both directions.",
      "Envelaro treats those steps as an onboarding sequence. The administrator provides the domains, mailbox requirements and current-provider context. We identify prerequisites, make the destination ready and show which actions belong to Envelaro, the customer administrator or the DNS provider.",
      "When the organization already has email, setup and migration are coordinated but not confused. Historical data can be prepared before live routing changes, and the previous provider should remain available until destination access, mail flow and the agreed migration scope have been validated.",
      "The result is not merely a collection of active accounts. It is an environment whose domain ownership, mailbox allocation, administrative access and delivery state can be explained and checked by the people responsible for operating it.",
    ],
    benefits: [
      ["Visible prerequisites", "Know which domain, plan, DNS and source-access requirements must be satisfied before mailboxes can operate."],
      ["Record-specific DNS guidance", "Change the records required for email while preserving website hosting, verification and application services that share the domain."],
      ["A documented handover", "Finish with tested user access, mail flow and administrator ownership instead of a collection of unexplained credentials."],
    ],
    fit: [
      "Businesses setting up professional domain email for the first time",
      "Teams without a dedicated internal email administrator",
      "Organizations replacing cPanel, GoDaddy or another existing provider",
      "Companies that need a controlled onboarding plan across several domains",
    ],
    process: [
      ["Discover", "Confirm domains, mailbox counts, aliases, current mail flow, third-party senders and any migration deadline."],
      ["Prepare", "Create the organization, verify ownership and provision the approved destination structure before changing routing."],
      ["Configure", "Publish and validate MX, SPF, DKIM and DMARC records with clear customer approval for DNS changes."],
      ["Prove and hand over", "Test inbound and outbound delivery, user access and administrative controls, then document the agreed next steps."],
    ],
    related: [
      ["Start with a domain check", "/tools/email-health", "See the public email records currently published for your domain."],
      ["Review the migration runbook", "/migration", "Understand how existing mailbox data and DNS cutover are sequenced."],
      ["Discuss your setup", "/contact", "Share your provider, domains, mailbox count and target date."],
    ],
  },
} satisfies Record<string, SolutionLandingContent>;

function SolutionLandingPage({ content }: { content: SolutionLandingContent }) {
  return (
    <main className="solution-landing">
      <PageHero title={content.title} copy={content.description}>
        <div className="button-row page-hero__actions">
          <ButtonLink href="/pricing">Compare plans</ButtonLink>
          <ButtonLink href="/contact" variant="secondary">Discuss your setup</ButtonLink>
        </div>
      </PageHero>
      <section className="solution-intro">
        <div className="container solution-intro__grid">
          <div>
            <span className="migration-kicker">{content.eyebrow}</span>
            <h2>{content.promise}</h2>
          </div>
          <div className="solution-intro__copy">
            {content.overview.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          </div>
        </div>
      </section>
      <section className="solution-benefits">
        <div className="container">
          <div className="section-heading section-heading--wide">
            <div><span className="eyebrow">What the service solves</span><h2>Professional email needs more than a branded address.</h2></div>
          </div>
          <div className="solution-card-grid">
            {content.benefits.map(([title, copy], index) => (
              <article key={title}><span>0{index + 1}</span><h3>{title}</h3><p>{copy}</p></article>
            ))}
          </div>
        </div>
      </section>
      <section className="solution-fit">
        <div className="container solution-fit__grid">
          <div><span className="migration-kicker">Designed for a clear use case</span><h2>Who this approach fits.</h2><p>Envelaro is intended for organizations managing their own business domains. Third-party resale and bulk unsolicited sending are not included.</p></div>
          <ul>{content.fit.map((item) => <li key={item}><CheckCircle2 aria-hidden="true" />{item}</li>)}</ul>
        </div>
      </section>
      <section className="solution-process">
        <div className="container">
          <div className="section-heading section-heading--wide">
            <div><span className="eyebrow">A managed path</span><h2>Four checkpoints from decision to dependable email.</h2></div>
          </div>
          <div className="solution-process__grid">
            {content.process.map(([title, copy], index) => <article key={title}><span>0{index + 1}</span><h3>{title}</h3><p>{copy}</p></article>)}
          </div>
        </div>
      </section>
      <RelatedMarketingLinks links={content.related} />
      <FinalCta />
    </main>
  );
}

function RelatedMarketingLinks({ links }: { links: readonly (readonly [string, string, string])[] }) {
  return (
    <section className="marketing-related">
      <div className="container">
        <div><span className="migration-kicker">Continue your evaluation</span><h2>Plan the next decision.</h2></div>
        <div className="marketing-related__grid">
          {links.map(([title, href, copy]) => <Link href={href} key={href}><strong>{title}</strong><span>{copy}</span><ArrowRight aria-hidden="true" /></Link>)}
        </div>
      </div>
    </section>
  );
}

type ProviderMigrationContent = {
  slug: string;
  provider: string;
  title: string;
  description: string;
  context: string[];
  inventory: readonly string[];
  cautions: readonly (readonly [string, string])[];
  outcome: string;
};

const providerMigrationContent = {
  cpanel: {
    slug: "cpanel",
    provider: "cPanel",
    title: "Migrate cPanel email to Envelaro with the website left in place.",
    description: "Move compatible mailbox data and mail routing away from cPanel while preserving the website, domain ownership and unrelated DNS services.",
    context: [
      "Email bundled with cPanel is often tied to a web-hosting account, but the website and the mailboxes do not have to move together. Envelaro first identifies where DNS is hosted, which cPanel server currently receives mail and whether each mailbox is accessible through compatible IMAP credentials.",
      "Mailbox messages and folders are only part of the inventory. Forwarders, aliases, autoresponders, catch-all behavior and application senders may be configured separately in cPanel. They must be recorded and deliberately recreated or retired rather than assumed to move with the mailbox data.",
      "The website's A, AAAA, CNAME and verification records remain separate from the email cutover. MX and related authentication records change only after destination mailboxes are ready and the administrator has approved the routing plan.",
    ],
    inventory: ["cPanel hostname and administrator or mailbox access", "Every active mailbox and estimated data volume", "Forwarders, aliases, groups and autoresponders", "Website forms, scanners and applications that send email", "Current MX, SPF, DKIM and DMARC records"],
    cautions: [["Do not cancel web hosting early", "The website, DNS zone or source mailbox data may still depend on the account even after new mail begins arriving at Envelaro."], ["Test application sending", "A website contact form may use the old server even when employee mailboxes have moved."], ["Keep a validation window", "Compare folders and recent messages, then run the agreed final synchronization before source-mailbox removal."]],
    outcome: "A completed cPanel migration leaves the website and non-mail DNS records unchanged, gives every approved user working access to the destination mailbox, preserves the eligible historical folders agreed in scope and routes new mail through Envelaro. The administrator should also know which cPanel forwarders or application senders were recreated, replaced or deliberately retired. Source hosting remains available until those results and the final synchronization have been signed off.",
  },
  godaddy: {
    slug: "godaddy",
    provider: "GoDaddy",
    title: "Move business email from GoDaddy after identifying the real mailbox platform.",
    description: "Separate the registrar, DNS and mailbox responsibilities before migrating mail so the domain and website continue operating normally.",
    context: [
      "A GoDaddy account may contain the domain registration, DNS hosting, website hosting and email subscription, but those services are not the same system. The mailbox itself may be Microsoft 365 or another legacy mail service. Envelaro confirms the actual source before selecting an access and migration method.",
      "The domain does not need to be transferred away from GoDaddy simply to use Envelaro email. An authorized administrator can keep registration and DNS there while publishing the required verification, MX and authentication records for the new service.",
      "Subscription cancellation is a closeout step, not the beginning of the migration. Existing mailboxes and administration should remain available until destination access, historical data, inbound delivery and outbound authentication have been validated.",
    ],
    inventory: ["The GoDaddy products attached to the domain", "Whether email is Microsoft 365 or another mailbox platform", "Mailbox, alias and shared-address list", "DNS access and current nameservers", "Renewal dates and source-service cancellation conditions"],
    cautions: [["Identify Microsoft 365 first", "A GoDaddy-branded Microsoft subscription can require a different authorization and closeout path from a standard IMAP mailbox."], ["Keep the registrar separate", "Changing mail providers does not require giving up ownership or registration of the domain."], ["Preserve unrelated DNS", "Website, validation and application records should not be replaced when the mail-routing records change."]],
    outcome: "A successful GoDaddy migration keeps the domain registered to the customer, preserves the website and required DNS services, and gives users verified access to Envelaro before the old email product is cancelled. Current messages should route to Envelaro, eligible historical folders should match the agreed scope, and the administrator should have a written decision for every alias, shared address and application sender discovered in the GoDaddy account.",
  },
  googleWorkspace: {
    slug: "google-workspace",
    provider: "Google Workspace",
    title: "Plan a Google Workspace email migration without treating every service as mail.",
    description: "Assess Gmail mailbox data, administrator authorization and the separate handling required for calendars, contacts, groups and Drive content.",
    context: [
      "A Google Workspace organization contains more than Gmail. An email migration can copy eligible messages and folders, but it does not automatically transfer Drive files, Docs, Chat history, identity settings, applications or every collaboration feature associated with the Google account.",
      "Envelaro confirms whether an administrator-authorized method is available for the required mailbox scope. The migration plan records users, aliases, groups, shared addresses, mailbox volume and any calendar or contact requirements before the first copy begins.",
      "Google Workspace can continue receiving current mail while the destination is prepared and an initial eligible data copy runs. MX records change only after the Envelaro domains, mailboxes and user access have been reviewed. A final synchronization and agreed coexistence window reduce the chance of leaving recent messages behind.",
    ],
    inventory: ["Authorized Google Workspace administrator", "Users, aliases, Groups and suspended accounts", "Mailbox size and largest folders", "Calendar and contact requirements", "Drive, Vault, Chat or identity dependencies outside email"],
    cautions: [["Define email-only scope", "Drive, Docs, Chat, Vault and Google identity are separate workloads and are not implied by mailbox migration."], ["Review Groups behavior", "Distribution, collaborative inbox and permission behavior may need to be recreated differently."], ["Plan account closeout", "Users may need export, retention or access decisions before Google licences are removed."]],
    outcome: "A completed Google Workspace email migration proves that approved Gmail data is available in the destination, current mail reaches Envelaro and authorized users can send with the expected domain authentication. It also leaves an explicit disposition for Google Groups, calendars, contacts, Drive, Vault and identity dependencies. Workspace licences should remain active until the final eligible data synchronization and every non-email retention or export decision has been completed.",
  },
  microsoft365: {
    slug: "microsoft-365",
    provider: "Microsoft 365",
    title: "Migrate Microsoft 365 email with shared resources and identity dependencies mapped first.",
    description: "Prepare Exchange Online mailbox transfer while treating calendars, shared mailboxes, OneDrive, Teams and Microsoft identity as separate scope decisions.",
    context: [
      "Microsoft 365 email normally runs through Exchange Online, but the same tenant may also control Entra identities, Teams, SharePoint, OneDrive, Office licences and device policies. Moving mail does not automatically replace those services or remove the need for a deliberate identity plan.",
      "Envelaro begins with authorized source access and an inventory of user mailboxes, shared mailboxes, aliases, distribution lists, mailbox sizes and delegated permissions. Calendar and contact portability is assessed separately from messages and folders.",
      "The source tenant remains active while the Envelaro destination is provisioned and eligible data is prepared. Mail-routing and sender-authentication changes are staged for an approved window, followed by delivery checks, final synchronization and a documented decision about remaining Microsoft services.",
    ],
    inventory: ["Authorized Microsoft 365 administrator", "User and shared mailboxes with delegated access", "Aliases, distribution lists and mail-flow rules", "Calendar, contact and archive requirements", "Teams, OneDrive, SharePoint and Entra identity dependencies"],
    cautions: [["Do not remove identities blindly", "A Microsoft account may still control device sign-in, Office licensing or access to non-email data."], ["Map shared mailboxes", "Delegation and send-as behavior need explicit destination decisions rather than a simple message copy."], ["Check third-party sending", "Applications using Microsoft connectors or SMTP settings require a separate update and test."]],
    outcome: "A completed Microsoft 365 email migration confirms approved Exchange mailbox data, destination access, inbound routing and authenticated outbound delivery. Shared mailboxes, distribution lists, send-as permissions and application connectors should each have a recorded destination decision. The organization must also retain or replace any Entra sign-in, Office licensing, Teams, OneDrive and SharePoint dependency before removing users or subscriptions from the Microsoft tenant. Document the final tenant owner, retention period and rollback contact before the source environment is changed.",
  },
  zohoMail: {
    slug: "zoho-mail",
    provider: "Zoho Mail",
    title: "Move Zoho Mail to Envelaro with mailbox and workplace scope separated.",
    description: "Inventory Zoho mailboxes, aliases and organization settings while deciding separately how calendars, contacts and other Zoho applications are handled.",
    context: [
      "Zoho Mail can be used as a standalone email service or as part of Zoho Workplace and a wider collection of business applications. A mailbox migration focuses on eligible messages and folders; it does not automatically reproduce Streams, WorkDrive, CRM connections or every organization policy.",
      "The available migration method depends on the Zoho plan, source settings and administrator access. Envelaro confirms whether compatible IMAP or another approved access path is available, then records mailbox size, aliases, groups, forwarding rules and any calendar or contact requirements.",
      "Existing Zoho service should remain active through the initial copy and destination validation. After an approved MX cutover, the team verifies delivery and user access, completes the agreed final synchronization and only then plans subscription or account closeout.",
    ],
    inventory: ["Zoho organization administrator and plan details", "Users, aliases, groups and forwarding rules", "Mailbox size and retention settings", "Calendar and contact requirements", "Zoho Workplace, CRM or application dependencies"],
    cautions: [["Confirm source access", "IMAP availability and administrator migration options can depend on the active Zoho plan and security settings."], ["Separate workplace applications", "Streams, WorkDrive and connected Zoho services are outside a basic email-data transfer."], ["Retain the source until sign-off", "Do not remove licences before recent mail, folders, routing and user access have been checked."]],
    outcome: "A completed Zoho Mail migration gives approved users access to their Envelaro mailboxes, preserves eligible messages and folders within the agreed scope and confirms that new mail no longer depends on Zoho MX records. The administrator should have explicit decisions for aliases, groups, calendars, contacts, Streams, WorkDrive and connected applications. Zoho accounts remain available until final synchronization, validation and any required export or retention work are complete.",
  },
} satisfies Record<string, ProviderMigrationContent>;

function ProviderMigrationPage({ content }: { content: ProviderMigrationContent }) {
  const relatedProviders = Object.entries(providerMigrationContent)
    .filter(([, provider]) => provider.provider !== content.provider)
    .map(([, provider]) => [
      `Migrate from ${provider.provider}`,
      `/migration/${provider.slug}`,
      `Review the source-specific considerations for ${provider.provider}.`,
    ] as const);
  return (
    <main className="provider-migration">
      <PageHero title={content.title} copy={content.description}>
        <div className="button-row page-hero__actions">
          <ButtonLink href="/migration#move-existing-email">Request a migration review</ButtonLink>
          <ButtonLink href="/migration" variant="secondary">See the migration runbook</ButtonLink>
        </div>
      </PageHero>
      <section className="provider-migration__overview">
        <div className="container provider-migration__overview-grid">
          <div><span className="migration-kicker">Migrating from {content.provider}</span><h2>Understand the source before setting a cutover date.</h2></div>
          <div>{content.context.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</div>
        </div>
      </section>
      <section className="provider-migration__inventory">
        <div className="container provider-migration__inventory-grid">
          <div><span className="eyebrow">Migration discovery</span><h2>Bring these facts to the first assessment.</h2><p>A complete inventory lets Envelaro confirm the viable access method, responsibilities and validation plan before either provider is changed.</p></div>
          <ul>{content.inventory.map((item, index) => <li key={item}><span>0{index + 1}</span>{item}</li>)}</ul>
        </div>
      </section>
      <section className="provider-migration__cautions">
        <div className="container">
          <div className="section-heading section-heading--wide"><div><span className="eyebrow">Source-specific boundaries</span><h2>Three details that prevent an incomplete migration.</h2></div></div>
          <div className="solution-card-grid">{content.cautions.map(([title, copy], index) => <article key={title}><span>0{index + 1}</span><h3>{title}</h3><p>{copy}</p></article>)}</div>
        </div>
      </section>
      <section className="provider-migration__sequence">
        <div className="container">
          <div><span className="migration-kicker">Controlled sequence</span><h2>Prepare, copy, validate, cut over and close out.</h2><p>The old provider continues operating while Envelaro prepares the destination and runs the agreed initial data copy. The administrator approves DNS changes only after destination mailboxes and user access are ready. Inbound and outbound delivery are then tested before the source service is retired.</p><p><strong>Completion criteria:</strong> {content.outcome}</p></div>
          <ButtonLink href="/migration">Read all six migration checkpoints <ArrowRight size={17} /></ButtonLink>
        </div>
      </section>
      <RelatedMarketingLinks links={relatedProviders} />
      <FinalCta />
    </main>
  );
}

function FaqPage() {
  const sections = FAQ_SECTIONS;
  return (
    <main>
      <PageHero
        title="Questions answered before your email moves."
        copy="Clear answers about plans, mailboxes, migration, DNS, administration, security and support, without hiding important conditions in the small print."
      >
        <ButtonLink href="/contact">Ask a specific question</ButtonLink>
      </PageHero>
      <section className="faq-page">
        <div className="container faq-page__layout">
          <aside>
            <span className="migration-kicker">Envelaro FAQ</span>
            <h2>Browse by topic.</h2>
            <p>For requirements that depend on your provider, region or deployment model, contact us for a scope-specific answer.</p>
            <nav aria-label="FAQ topics">
              {sections.map((section, index) => <a href={`#faq-${index + 1}`} key={section.title}>{section.title}</a>)}
            </nav>
          </aside>
          <div className="faq-page__sections">
            {sections.map((section, index) => (
              <section id={`faq-${index + 1}`} key={section.title}>
                <span>0{index + 1}</span>
                <h2>{section.title}</h2>
                <div>
                  {section.questions.map(([question, answer]) => (
                    <details key={question}>
                      <summary>{question}<span>+</span></summary>
                      <p>{answer}</p>
                    </details>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>
      <FinalCta />
    </main>
  );
}

function AboutPage() {
  return (
    <main>
      <PageHero
        title="Business communication should be simpler to own."
        copy="Envelaro brings business email, calendar, contacts, tasks, chat, voice and video meetings and storage into one managed workspace, with clear administration and predictable organization-level plans."
      >
        <ButtonLink href="/contact">Talk to Envelaro</ButtonLink>
      </PageHero>
      <section className="company-page">
        <div className="container company-page__intro">
          <span className="migration-kicker">Why Envelaro exists</span>
          <h2>Fewer disconnected tools. More control for the organization.</h2>
          <p>Small and mid-sized organizations often choose between fragmented low-cost tools and complex enterprise suites. Envelaro serves the space between them: dependable communication, straightforward administration and support that treats migration and domain setup as operational work.</p>
        </div>
        <div className="container company-page__principles">
          <article><span>01</span><h3>Organization first</h3><p>Plans, domains and administration are designed around the business rather than a collection of unrelated personal accounts.</p></article>
          <article><span>02</span><h3>Operational clarity</h3><p>Setup, DNS, migration and service boundaries should be visible to the administrator, not hidden behind vague automation claims.</p></article>
          <article><span>03</span><h3>Trust without exaggeration</h3><p>We describe controls and certifications accurately, publish service documents and avoid claiming assurance that has not been independently earned.</p></article>
        </div>
        <div className="container company-page__facts">
          <div><span className="migration-kicker">Entity facts</span><h2>What Envelaro is—and what it is not.</h2><p>These facts provide a consistent public description for customers, search systems and service reviewers.</p></div>
          <dl>
            <div><dt>Brand and website</dt><dd>Envelaro · <a href="https://envelaro.com/">envelaro.com</a></dd></div>
            <div><dt>Service category</dt><dd>Managed business communications SaaS centered on custom-domain email.</dd></div>
            <div><dt>Intended customers</dt><dd>Organizations using domains they own or are authorized to administer.</dd></div>
            <div><dt>Availability</dt><dd>Approved business customers in supported regions, confirmed during onboarding.</dd></div>
            <div><dt>Support contact</dt><dd><a href="mailto:support@envelaro.com">support@envelaro.com</a></dd></div>
            <div><dt>Service boundary</dt><dd>A managed application service—not VPS, root, shell, relay or infrastructure-administration access.</dd></div>
          </dl>
        </div>
      </section>
      <FinalCta />
    </main>
  );
}

function StatusPage() {
  return (
    <main>
      <PageHero
        title="Service status and operational notices."
        copy="This page provides the current high-level availability statement for Envelaro services. A dedicated real-time incident dashboard and subscription notifications will be introduced as the service expands."
      />
      <section className="status-page">
        <div className="container status-page__panel">
          <div className="status-page__topline"><span className="status-page__indicator" aria-hidden="true" /><div><strong>No active public incident posted</strong><p>Last reviewed by the Envelaro operations team.</p></div></div>
          <div className="status-page__services">
            {[
              "Tenant portal",
              "Webmail",
              "Inbound and outbound mail",
              "Calendar, contacts and tasks",
              "Chat and video meetings",
              "File storage",
            ].map((service) => <div key={service}><span>{service}</span><strong>Operational</strong></div>)}
          </div>
          <p className="status-page__notice">This is a manually maintained public statement, not automated uptime telemetry. If you are experiencing a service issue, contact support so the operations team can investigate your tenant and region.</p>
          <div className="status-page__actions"><ButtonLink href="/contact">Report a service issue</ButtonLink><a href="/service-availability">Read the service availability policy</a></div>
        </div>
      </section>
    </main>
  );
}

const healthFindingContent: Record<
  EmailHealthFindingType,
  { label: string; icon: typeof CheckCircle2 }
> = {
  failed: { label: "Failed", icon: CircleX },
  unverified: { label: "Unverified", icon: Radar },
  recommended: { label: "Recommended", icon: CircleAlert },
  optional: { label: "Optional", icon: LifeBuoy },
  passed: { label: "Passed", icon: CheckCircle2 },
};

const healthFindingOrder: EmailHealthFindingType[] = [
  "failed",
  "unverified",
  "recommended",
  "optional",
  "passed",
];

type HealthCheck = EmailHealthResult["checks"][number];

function EvidenceDetails({ check }: { check: HealthCheck }) {
  return (
    <details className="health-evidence">
      <summary>
        Verification evidence
        <span>{check.evidence.length} {check.evidence.length === 1 ? "query" : "queries"}</span>
      </summary>
      <div className="health-evidence__body">
        {check.evidence.length ? check.evidence.map((entry) => (
          <div className="health-evidence__query" key={`${entry.query}:${entry.type}`}>
            <div className="health-evidence__meta">
              <code>{entry.type} {entry.query}</code>
              <span>{entry.responseStatus}</span>
              <span>{entry.lookupMs} ms</span>
              <span>TTL {entry.ttlSeconds === null ? "not returned" : `${entry.ttlSeconds}s`}</span>
              <span>{entry.dnssecValidated ? "DNSSEC validated" : "DNSSEC not validated"}</span>
            </div>
            <div className="health-evidence__records">
              {entry.records.length
                ? entry.records.map((record) => <code key={record}>{record}</code>)
                : <code>No matching record returned</code>}
            </div>
            {entry.note ? <p>{entry.note}</p> : null}
            <small>Observed via {entry.resolver} at scan time.</small>
          </div>
        )) : (
          <p>No DNS query was performed for this result.</p>
        )}
      </div>
    </details>
  );
}

function ScoreBreakdown({ result }: { result: EmailHealthResult }) {
  return (
    <section className="health-score-card">
      <div className="health-score-card__heading">
        <div>
          <span>Scoring formula</span>
          <h3>{result.scoreSummary.startingScore} starting points</h3>
        </div>
        <strong>-{result.scoreSummary.startingScore - result.scoreSummary.finalScore}</strong>
      </div>
      <div className="health-score-card__formula">
        {result.scoreSummary.deductions.length ? result.scoreSummary.deductions.map((deduction) => (
          <div key={deduction.checkId}>
            <span>{deduction.title}<small>{deduction.reason}</small></span>
            <strong>-{deduction.points}</strong>
          </div>
        )) : <p>No score deductions were applied.</p>}
      </div>
      <div className="health-score-card__total">
        <span>Final score</span>
        <strong>
          {result.scoreSummary.startingScore}
          {" - "}
          {result.scoreSummary.startingScore - result.scoreSummary.finalScore}
          {" = "}
          {result.scoreSummary.finalScore}/100
        </strong>
      </div>
      <p>
        Unverified and optional items deduct zero points. Recommendations are
        weighted by risk; failed controls carry the largest deductions.
      </p>
    </section>
  );
}

function RemediationPlan({
  result,
  copiedValue,
  onCopy,
}: {
  result: EmailHealthResult;
  copiedValue: string | null;
  onCopy: (value: string) => void;
}) {
  return (
    <section className="health-remediation">
      <div className="health-remediation__heading">
        <div>
          <span>Prioritized remediation</span>
          <h2>What to fix, who should own it, and what to publish.</h2>
        </div>
        <p>{result.remediation.length} actions ordered by finding class and risk.</p>
      </div>
      <div className="health-remediation__list">
        {result.remediation.map((item) => (
          <article key={`${item.priority}:${item.checkId}`}>
            <div className="health-remediation__priority">{String(item.priority).padStart(2, "0")}</div>
            <div className="health-remediation__content">
              <div className="health-remediation__title">
                <h3>{item.title}</h3>
                <span className={`health-risk health-risk--${item.risk}`}>{item.risk} risk</span>
              </div>
              <div className="health-remediation__meta">
                <span><strong>Owner</strong>{item.owner}</span>
                <span><strong>Effort</strong>{item.effort}</span>
                <span><strong>Record</strong>{item.recordType ?? "Provider action"} {item.recordName ?? ""}</span>
              </div>
              {item.copyReadyValue ? (
                <div className="health-remediation__value">
                  <code>{item.copyReadyValue}</code>
                  <button
                    aria-label={`Copy DNS value for ${item.title}`}
                    onClick={() => onCopy(item.copyReadyValue as string)}
                    type="button"
                  >
                    {copiedValue === item.copyReadyValue ? <Check size={16} /> : <Copy size={16} />}
                    {copiedValue === item.copyReadyValue ? "Copied" : "Copy value"}
                  </button>
                </div>
              ) : (
                <p className="health-remediation__provider">
                  Provider-specific value required; confirm it with the listed owner.
                </p>
              )}
              <p className="health-remediation__caution">{item.caution}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function EmailHealthCheckPage() {
  const [domain, setDomain] = useState("");
  const [dkimSelector, setDkimSelector] = useState("");
  const [result, setResult] = useState<EmailHealthResult | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState("");
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [leadError, setLeadError] = useState("");
  const [copiedValue, setCopiedValue] = useState<string | null>(null);
  const [startedAt] = useState(() => Date.now());

  async function handleScan(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!emailHealthEnabled) return;
    setScanning(true);
    setScanError("");
    setResult(null);
    setLeadSubmitted(false);
    setSubmittedEmail("");
    try {
      const nextResult = await scanPublicEmailDns({ domain, dkimSelector });
      setDomain(nextResult.domain);
      setResult(nextResult);
    } catch (error) {
      setScanError(
        error instanceof Error
          ? error.message
          : "The DNS scan could not be completed. Please try again.",
      );
    } finally {
      setScanning(false);
    }
  }

  async function handleHelpRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!result || !launchListEnabled) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    const failedCount = result.checks.filter((check) => check.findingType === "failed").length;
    const recommendedCount = result.checks.filter((check) => check.findingType === "recommended").length;
    setLeadSubmitting(true);
    setLeadError("");
    try {
      await submitLaunchLead({
        name: String(data.get("name") ?? ""),
        email: String(data.get("email") ?? ""),
        company: String(data.get("company") ?? ""),
        teamSize: String(data.get("teamSize") ?? ""),
        currentProvider: result.mailProvider ?? "",
        message: `Email health review requested for ${result.domain}. Grade ${result.grade} (${result.score}/100), with ${failedCount} failed and ${recommendedCount} recommended findings.`,
        consent: true,
        privacyVersion: "website-privacy-2026-07-28",
        source: "email_health_check",
        scannedDomain: result.domain,
        dkimSelector: result.dkimSelector ?? dkimSelector,
        website: String(data.get("website") ?? ""),
        elapsedMs: Date.now() - startedAt,
      });
      setSubmittedEmail(String(data.get("email") ?? ""));
      setLeadSubmitted(true);
      form.reset();
    } catch (error) {
      setLeadError(
        error instanceof Error
          ? error.message
          : "We could not save your request. Please try again.",
      );
    } finally {
      setLeadSubmitting(false);
    }
  }

  async function handleCopy(value: string) {
    await navigator.clipboard.writeText(value);
    setCopiedValue(value);
    window.setTimeout(() => setCopiedValue(null), 1800);
  }

  const findingCounts = Object.fromEntries(
    healthFindingOrder.map((type) => [
      type,
      result?.checks.filter((check) => check.findingType === type).length ?? 0,
    ]),
  ) as Record<EmailHealthFindingType, number>;

  return (
    <main className="health-page">
      <section className="health-hero">
        <div className="container health-hero__grid">
          <div className="health-hero__copy">
            <h1>Is your business email configured correctly?</h1>
            <p>
              Check the public records that route your mail and protect your
              domain from impersonation. No account, password or DNS access is
              required.
            </p>
            <ul>
              <li><CheckCircle2 size={18} />18 public security and delivery controls</li>
              <li><CheckCircle2 size={18} />Spoofing, TLS and reporting exposure</li>
              <li><CheckCircle2 size={18} />Prioritized actions for every finding</li>
            </ul>
          </div>
          <form className="health-scan-form" onSubmit={handleScan}>
            <div>
              <label htmlFor="health-domain">Business domain</label>
              <div className="health-domain-input">
                <Globe2 aria-hidden="true" size={20} />
                <input
                  autoCapitalize="none"
                  autoComplete="off"
                  id="health-domain"
                  inputMode="url"
                  name="domain"
                  onChange={(event) => setDomain(event.target.value)}
                  placeholder="example.com"
                  required
                  spellCheck={false}
                  value={domain}
                />
              </div>
            </div>
            <div>
              <label htmlFor="health-selector">
                DKIM selector <span>optional</span>
              </label>
              <input
                autoCapitalize="none"
                autoComplete="off"
                id="health-selector"
                maxLength={63}
                name="dkimSelector"
                onChange={(event) => setDkimSelector(event.target.value)}
                placeholder="For example, google or selector1"
                spellCheck={false}
                value={dkimSelector}
              />
              <small>Your email provider can tell you which selector it uses.</small>
            </div>
            <button
              className="button button--primary"
              disabled={!emailHealthEnabled || scanning}
              type="submit"
            >
              {scanning ? "Checking DNS..." : "Check my domain"}
              {scanning ? <RotateCcw className="health-spinner" size={18} /> : <Search size={18} />}
            </button>
            <p className="health-privacy">
              We query public DNS records only. We never request mailbox or DNS credentials.
            </p>
            {!emailHealthEnabled ? (
              <p className="form-note form-note--pending">
                The public checker is temporarily unavailable.
              </p>
            ) : null}
            {scanError ? <p className="form-note form-note--error" role="alert">{scanError}</p> : null}
          </form>
        </div>
      </section>

      {result ? (
        <section className="health-results" aria-live="polite">
          <div className="container">
            <div className="health-results__label">
              <span>Results summary</span>
              <p>Scanned {new Date(result.checkedAt).toLocaleString()}</p>
            </div>
            <div className="health-results__summary">
              <div className={`health-grade health-grade--${result.grade.toLowerCase()}`}>
                <span>Domain grade</span>
                <strong>{result.grade}</strong>
                <small>{result.score}/100</small>
              </div>
              <div className="health-results__intro">
                <span>Results for {result.domain}</span>
                <h2>
                  {findingCounts.failed
                    ? `${findingCounts.failed} failed ${findingCounts.failed === 1 ? "control needs" : "controls need"} attention.`
                    : findingCounts.recommended
                      ? "No failed controls, with improvements still recommended."
                      : "The verified public controls look healthy."}
                </h2>
                <p>
                  This report checks 18 publicly observable controls. It does not
                  inspect mailbox content, passwords, sender reputation, blocklists,
                  live SMTP negotiation or actual message delivery.
                </p>
                <div className="health-provider-list">
                  <span><Mail size={17} />Mail: {result.mailProvider ?? "Provider not identified"}</span>
                  <span><ServerCog size={17} />DNS: {result.dnsProvider ?? "Provider not identified"}</span>
                </div>
              </div>
            </div>

            <div className="health-coverage" aria-label="Audit coverage summary">
              {healthFindingOrder.map((type) => (
                <div className={`health-coverage--${type}`} key={type}>
                  <strong>{findingCounts[type]}</strong>
                  <span>{healthFindingContent[type].label}</span>
                </div>
              ))}
            </div>

            {leadSubmitted ? (
              <>
                <div className="health-report-delivered" role="status">
                  <CheckCircle2 aria-hidden="true" size={23} />
                  <div>
                    <strong>Your detailed report is ready.</strong>
                    <span>We emailed a copy to {submittedEmail}. The full report is also unlocked below.</span>
                  </div>
                </div>

                <ScoreBreakdown result={result} />

            <RemediationPlan
              copiedValue={copiedValue}
              onCopy={(value) => void handleCopy(value)}
              result={result}
            />

            <div className="health-findings">
              <div className="health-findings__heading">
                <span>Detailed findings</span>
                <h2>Every result, separated by what it means.</h2>
                <p>{result.evidenceSource}</p>
              </div>
              {healthFindingOrder.map((findingType, findingIndex) => {
                const findingChecks = result.checks.filter((check) => check.findingType === findingType);
                if (!findingChecks.length) return null;
                const findingContent = healthFindingContent[findingType];
                return (
                  <section className={`health-category health-category--${findingType}`} key={findingType}>
                    <div className="health-category__heading">
                      <div>
                        <span>{String(findingIndex + 1).padStart(2, "0")}</span>
                        <h2>{findingContent.label}</h2>
                      </div>
                      <p>{findingChecks.length} {findingChecks.length === 1 ? "finding" : "findings"}</p>
                    </div>
                    <div className="health-check-list">
                      {findingChecks.map((check) => {
                        const StatusIcon = findingContent.icon;
                        return (
                          <article className={`health-check health-check--${check.findingType}`} key={check.id}>
                            <div className="health-check__icon"><StatusIcon aria-hidden="true" size={23} /></div>
                            <div>
                              <div className="health-check__topline">
                                <div>
                                  <span>{check.category}</span>
                                  <h3>{check.title}</h3>
                                </div>
                                <div className="health-check__labels">
                                  <span>{findingContent.label}</span>
                                  <span className={`health-risk health-risk--${check.risk}`}>{check.risk} risk</span>
                                </div>
                              </div>
                              <strong>{check.summary}</strong>
                              <p>{check.detail}</p>
                              <p className="health-check__recommendation">
                                <ArrowRight aria-hidden="true" size={16} />
                                <span><strong>Recommended:</strong> {check.recommendation}</span>
                              </p>
                              <EvidenceDetails check={check} />
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
                </div>
              </>
            ) : null}

            {!leadSubmitted ? (
              <section className="health-report-gate" aria-labelledby="health-report-gate-title">
                <div aria-hidden="true" className="health-report-gate__preview">
                  <div className="health-report-gate__preview-heading" />
                  <div className="health-report-gate__preview-grid">
                    <span /><span /><span />
                  </div>
                  <div className="health-report-gate__preview-row" />
                  <div className="health-report-gate__preview-row" />
                  <div className="health-report-gate__preview-row" />
                </div>
                <div className="health-report-gate__overlay">
                  <div className="health-report-gate__copy">
                    <span className="health-report-gate__icon"><LockKeyhole aria-hidden="true" size={22} /></span>
                    <div>
                      <h2 id="health-report-gate-title">Get the complete report by email</h2>
                      <p>
                        Receive the scoring breakdown, verification evidence,
                        prioritized recommendations, owners and copy-ready DNS values.
                      </p>
                    </div>
                  </div>
                  <form className="health-lead-form health-lead-form--gate" onSubmit={handleHelpRequest}>
                <label>Name<input autoComplete="name" minLength={2} name="name" required /></label>
                <label>Work email<input autoComplete="email" name="email" required type="email" /></label>
                <label>Company<input autoComplete="organization" maxLength={140} name="company" /></label>
                <label>Team size
                  <select defaultValue="" name="teamSize" required>
                    <option disabled value="">Choose a range</option>
                    <option value="1-5">1-5</option>
                    <option value="6-20">6-20</option>
                    <option value="21-50">21-50</option>
                    <option value="51-200">51-200</option>
                    <option value="200+">More than 200</option>
                  </select>
                </label>
                <label className="contact-form__honeypot" aria-hidden="true">
                  Website<input autoComplete="off" name="website" tabIndex={-1} />
                </label>
                <label className="health-lead-form__consent">
                  <input name="consent" required type="checkbox" />
                  <span>
                    Email me this report and allow Envelaro to contact me about
                    this domain. See our <Link href="/privacy">privacy notice</Link>.
                  </span>
                </label>
                <div className="health-lead-form__actions">
                  <button
                    className="button button--primary"
                    disabled={!launchListEnabled || leadSubmitting}
                    type="submit"
                  >
                    {leadSubmitting ? "Preparing your report..." : "Email me the detailed report"}
                    <ArrowRight size={18} />
                  </button>
                  <span>No password or DNS access required.</span>
                </div>
                {!launchListEnabled ? (
                  <p className="form-note form-note--pending">
                    Email delivery is temporarily unavailable.
                  </p>
                ) : null}
                {leadError ? <p className="form-note form-note--error" role="alert">{leadError}</p> : null}
                  </form>
                </div>
              </section>
            ) : null}
          </div>
        </section>
      ) : null}

      <section className="health-explainer">
        <div className="container health-explainer__grid">
          <h2>What the check tells you.</h2>
          <div>
            <p><strong>MX</strong> identifies where incoming mail should be delivered.</p>
            <p><strong>SPF</strong> checks authorization, enforcement, lookup limits and deprecated mechanisms.</p>
            <p><strong>DMARC</strong> checks enforcement, coverage, reporting, alignment and subdomain protection.</p>
            <p><strong>DKIM</strong> verifies the supplied selector and detects test-mode or inactive keys.</p>
            <p><strong>MTA-STS</strong> advertises a policy for authenticated TLS mail delivery.</p>
            <p><strong>TLS-RPT</strong> provides visibility into transport-security failures.</p>
            <p><strong>CAA</strong> limits which certificate authorities may issue for the domain.</p>
            <p><strong>BIMI</strong> supports verified brand presentation in participating inboxes.</p>
          </div>
        </div>
      </section>
    </main>
  );
}

const privateDeploymentModels = [
  {
    label: "Available now",
    title: "Dedicated mail infrastructure",
    copy: "A dedicated mail environment for your organization, operated by Envelaro and connected to the Envelaro administration experience.",
    points: ["Dedicated mail server and sending IP", "Isolated mail storage and queues", "Managed monitoring, backup and recovery planning"],
  },
  {
    label: "Custom engagement",
    title: "Envelaro private cloud",
    copy: "A more isolated deployment designed around regional, security and operational requirements after an architecture review.",
    points: ["Separate application data and secrets", "Regional hosting options", "Custom identity, retention and support design"],
  },
  {
    label: "Discovery required",
    title: "Customer-cloud deployment",
    copy: "For organizations that need infrastructure in their own cloud account, with a jointly defined responsibility and operations model.",
    points: ["Customer-owned cloud account", "Customer-controlled networking and keys", "Restricted Envelaro operational access"],
  },
];

const privateDeploymentStages = [
  ["01", "Discovery", "Confirm users, domains, current provider, data volume and business constraints."],
  ["02", "Architecture", "Agree the deployment boundary, region, identity, retention, backup and support model."],
  ["03", "Migration plan", "Inventory mailboxes, prepare coexistence where needed and define a rollback-safe cutover."],
  ["04", "Provisioning", "Build the environment, configure security controls and complete acceptance checks."],
  ["05", "Cutover", "Migrate data, update DNS in controlled stages and validate inbound and outbound delivery."],
  ["06", "Managed operations", "Monitor the service, manage incidents, maintain backups and review capacity."],
];

function ManagedPrivateDeploymentPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [startedAt] = useState(() => Date.now());

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!launchListEnabled) return;

    setSubmitting(true);
    setError("");

    const form = event.currentTarget;
    const data = new FormData(form);
    const value = (name: string) => String(data.get(name) || "Not specified");
    const message = [
      "Lead type: Managed private deployment",
      `Deployment preference: ${value("deploymentModel")}`,
      `Number of domains: ${value("domains")}`,
      `Estimated storage: ${value("storageNeed")}`,
      `Preferred region: ${value("preferredRegion")}`,
      `Identity provider: ${value("identityProvider")}`,
      `Target timeline: ${value("timeline")}`,
      `Security, compliance and retention: ${value("securityRequirements")}`,
      `Migration scope and other requirements: ${value("message")}`,
    ].join("\n");

    try {
      await submitLaunchLead({
        name: value("name"),
        email: value("email"),
        company: value("company"),
        teamSize: value("teamSize"),
        currentProvider: value("currentProvider"),
        message,
        consent: true,
        privacyVersion: "website-privacy-2026-07-28",
        source: "website_contact",
        leadType: "managed_private_deployment",
        deploymentRequirements: {
          deploymentModel: value("deploymentModel"),
          domains: value("domains"),
          storageNeed: value("storageNeed"),
          preferredRegion: value("preferredRegion"),
          identityProvider: value("identityProvider"),
          timeline: value("timeline"),
          securityRequirements: value("securityRequirements"),
          migrationRequirements: value("message"),
        },
        website: String(data.get("website") || ""),
        elapsedMs: Date.now() - startedAt,
      });
      setSubmitted(true);
      form.reset();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "We could not submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="private-deployment-page">
      <PageHero
        title={<>Dedicated infrastructure. Envelaro simplicity.</>}
        copy="Deploy business email and workday tools around your organization's infrastructure, migration, regional and operational requirements, without assembling and maintaining the platform yourself."
      >
        <ButtonLink href="#private-deployment-form" external>
          Request a deployment review <ArrowRight size={16} />
        </ButtonLink>
      </PageHero>

      <section className="section private-deployment-overview">
        <div className="section-heading section-heading--wide">
          <span className="eyebrow">Deployment models</span>
          <h2>Choose the level of isolation your organization needs.</h2>
          <p>We start with technical discovery, then confirm feasibility, responsibilities, delivery scope and commercial terms before making commitments.</p>
        </div>
        <div className="private-deployment-models">
          {privateDeploymentModels.map((model, index) => (
            <article className={`private-deployment-model${index === 0 ? " private-deployment-model--recommended" : ""}`} key={model.title}>
              <span className="private-deployment-model__label">{model.label}</span>
              <h3>{model.title}</h3>
              <p>{model.copy}</p>
              <ul>
                {model.points.map((point) => <li key={point}><CheckCircle2 size={17} /> {point}</li>)}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="section private-deployment-included">
        <div className="private-deployment-included__copy">
          <span className="eyebrow">Designed around your environment</span>
          <h2>More than a separate server.</h2>
          <p>The engagement covers the decisions that determine whether a private deployment is reliable in production, not only where the software runs.</p>
          <div className="private-deployment-checks">
            {["Dedicated environment and sending reputation plan", "Mailbox and historical email migration planning", "Backup, recovery and retention options", "Regional hosting and data-location review", "Identity and administrator access design", "Enterprise support and service-level options"].map((item) => (
              <span key={item}><CheckCircle2 size={18} /> {item}</span>
            ))}
          </div>
        </div>
        <aside className="private-deployment-boundary">
          <ShieldCheck size={30} />
          <span className="eyebrow">Clear operating boundary</span>
          <h3>Managed by Envelaro, designed with your IT team.</h3>
          <p>This is a managed deployment offering, not a downloadable, customer-operated self-hosting package. Ownership, administrative access, incident response and change management are defined during discovery.</p>
          <p>Security certifications or compliance mappings are confirmed only when they are contractually applicable to the selected deployment.</p>
        </aside>
      </section>

      <section className="section private-deployment-process">
        <div className="section-heading">
          <span className="eyebrow">Delivery path</span>
          <h2>A controlled route from discovery to operations.</h2>
        </div>
        <div className="private-deployment-process__grid">
          {privateDeploymentStages.map(([number, title, copy]) => (
            <article key={number}>
              <span>{number}</span>
              <h3>{title}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="contact-section private-deployment-contact" id="private-deployment-form">
        <div className="private-deployment-contact__intro">
          <span className="eyebrow">Requirements review</span>
          <h2>Tell us what your deployment must support.</h2>
          <p>Share your current environment and constraints. We will use this information to prepare the first architecture and migration discussion.</p>
          <div className="private-deployment-contact__note">
            <Mail size={20} />
            <span>No automatic purchase or provisioning. Your request is reviewed by the Envelaro team first.</span>
          </div>
        </div>

        {submitted ? (
          <div className="contact-success">
            <CheckCircle2 size={36} />
            <h3>Requirements received.</h3>
            <p>We will review the deployment details and contact you to arrange the next discussion.</p>
          </div>
        ) : (
          <form className="contact-form private-deployment-form" onSubmit={handleSubmit}>
            <label>Full name<input name="name" type="text" autoComplete="name" required /></label>
            <label>Work email<input name="email" type="email" autoComplete="email" required /></label>
            <label>Company<input name="company" type="text" autoComplete="organization" required /></label>
            <label>Expected mailboxes<select name="teamSize" required defaultValue=""><option value="" disabled>Select a range</option><option>100-250</option><option>251-500</option><option>501-1,000</option><option>1,001-5,000</option><option>5,000+</option></select></label>
            <label>Current email provider<input name="currentProvider" type="text" placeholder="Microsoft 365, Google Workspace, other" required /></label>
            <label>Deployment preference<select name="deploymentModel" required defaultValue=""><option value="" disabled>Select a model</option><option>Dedicated mail infrastructure</option><option>Envelaro private cloud</option><option>Customer-cloud deployment</option><option>Need guidance</option></select></label>
            <label>Number of domains<input name="domains" type="text" placeholder="For example, 4" /></label>
            <label>Estimated email storage<input name="storageNeed" type="text" placeholder="For example, 8 TB total" /></label>
            <label>Preferred hosting region<input name="preferredRegion" type="text" placeholder="Country or region" /></label>
            <label>Identity provider<input name="identityProvider" type="text" placeholder="Microsoft Entra ID, Okta, other" /></label>
            <label className="contact-form__wide">Target timeline<select name="timeline" defaultValue=""><option value="" disabled>Select a timeline</option><option>Within 30 days</option><option>1-3 months</option><option>3-6 months</option><option>6+ months</option><option>Exploring options</option></select></label>
            <label className="contact-form__wide">Security, compliance and retention requirements<textarea name="securityRequirements" rows={4} placeholder="Required controls, certifications, retention periods, data-location or audit needs" /></label>
            <label className="contact-form__wide">Migration scope and other requirements<textarea name="message" rows={5} placeholder="Mailbox sources, coexistence needs, expected cutover window, integrations and support requirements" required /></label>
            <label className="contact-form__honeypot" aria-hidden="true">Website<input name="website" type="text" tabIndex={-1} autoComplete="off" /></label>
            <label className="contact-form__consent contact-form__wide"><input name="consent" type="checkbox" required /> <span>I agree that Envelaro may contact me about this request and process these details according to the privacy policy.</span></label>
            {error && <p className="contact-form__error contact-form__wide">{error}</p>}
            <button className="button button--primary contact-form__wide" type="submit" disabled={submitting || !launchListEnabled}>
              {submitting ? "Submitting..." : "Request deployment review"} <ArrowRight size={16} />
            </button>
          </form>
        )}
      </section>
    </main>
  );
}

function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [startedAt] = useState(() => Date.now());

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!launchListEnabled) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    setSubmitting(true);
    setError("");
    try {
      await submitLaunchLead({
        name: String(data.get("name") ?? ""),
        email: String(data.get("email") ?? ""),
        company: String(data.get("company") ?? ""),
        teamSize: String(data.get("teamSize") ?? ""),
        currentProvider: String(data.get("currentProvider") ?? ""),
        message: String(data.get("message") ?? ""),
        consent: true,
        privacyVersion: "website-privacy-2026-07-28",
        source: "website_contact",
        website: String(data.get("website") ?? ""),
        elapsedMs: Date.now() - startedAt,
      });
      setSubmitted(true);
      form.reset();
    } catch (nextError) {
      setError(
        nextError instanceof Error
          ? nextError.message
          : "We could not save your request. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main>
      <PageHero
        title="Talk to us about business email."
        copy="Tell us which plan you are considering, along with your domains, current provider, storage needs and expected mailbox usage. We will help determine the right fit."
      />
      <section className="contact-section">
        <div className="container contact-section__grid">
          <div>
            <h2>Plan your business email.</h2>
            <p>
              Share your current setup and migration requirements. We will use
              that context to discuss onboarding, capacity and responsible
              sending limits.
            </p>
          </div>
          <form className="contact-form" onSubmit={handleSubmit}>
            <label>Name<input autoComplete="name" name="name" required minLength={2} /></label>
            <label>Work email<input autoComplete="email" name="email" required type="email" /></label>
            <label>Company<input autoComplete="organization" name="company" maxLength={140} /></label>
            <label>Team size
              <select defaultValue="" name="teamSize" required>
                <option disabled value="">Choose a range</option>
                <option value="1-5">1-5</option><option value="6-20">6-20</option>
                <option value="21-50">21-50</option><option value="51-200">51-200</option>
                <option value="200+">More than 200</option>
              </select>
            </label>
            <label className="contact-form__wide">Current email provider
              <input name="currentProvider" maxLength={120} placeholder="For example, Google Workspace" />
            </label>
            <label className="contact-form__wide">What do you need from business email?
              <textarea name="message" required minLength={10} maxLength={3000} rows={5} />
            </label>
            <label className="contact-form__honeypot" aria-hidden="true">
              Website<input name="website" tabIndex={-1} autoComplete="off" />
            </label>
            <label className="contact-consent">
              <input name="consent" type="checkbox" required />
              <span>
                I agree that Envelaro may store these details and contact me about
                my enquiry. See our <Link href="/privacy">privacy notice</Link>.
              </span>
            </label>
            <button
              className="button button--primary"
              type="submit"
              disabled={!launchListEnabled || submitting || submitted}
            >
              {submitted ? "Enquiry sent" : submitting ? "Sending..." : "Send enquiry"}
              {!submitted ? <ArrowRight size={18} /> : <CheckCircle2 size={18} />}
            </button>
            {!launchListEnabled ? (
              <p className="form-note form-note--pending">
                Online enquiries are temporarily unavailable. Please try again
                later.
              </p>
            ) : null}
            {submitted ? <p className="form-note">Thanks. We received your enquiry and will be in touch.</p> : null}
            {error ? <p className="form-note form-note--error" role="alert">{error}</p> : null}
          </form>
        </div>
      </section>
    </main>
  );
}

function LegalPage({ document }: { document: LegalDocument }) {
  return (
    <main>
      <section className="legal-hero">
        <div className="container legal-hero__grid">
          <div>
            <Link className="legal-back" href="/">
              <ArrowRight aria-hidden="true" /> Envelaro home
            </Link>
            <h1>{document.title}</h1>
            <p>{document.summary}</p>
          </div>
          <dl className="legal-meta">
            <div><dt>Status</dt><dd>{document.status}</dd></div>
            <div><dt>Last updated</dt><dd>{document.effectiveDate}</dd></div>
          </dl>
        </div>
      </section>
      <section className="legal-document">
        <div className="container legal-document__grid">
          <aside className="legal-toc">
            <h2>On this page</h2>
            <nav aria-label={`${document.title} sections`}>
              {document.sections.map((section) => (
                <a href={`#${section.id}`} key={section.id}>{section.title}</a>
              ))}
            </nav>
          </aside>
          <article className="legal-copy">
            {document.highlights ? (
              <section className="legal-highlights" aria-label="Document overview">
                {document.highlights.map((highlight) => (
                  <div key={highlight.label}>
                    <span>{highlight.label}</span>
                    <strong>{highlight.value}</strong>
                  </div>
                ))}
              </section>
            ) : null}
            {document.sections.map((section) => (
              <section id={section.id} key={section.id}>
                <h2>{section.title}</h2>
                {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                {section.bullets ? (
                  <ul>
                    {section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
                  </ul>
                ) : null}
                {section.note ? <p className="legal-note">{section.note}</p> : null}
              </section>
            ))}
            <section id="contact">
              <h2>Contact</h2>
              <p>
                Questions, rights requests, or abuse reports can be submitted
                through our <Link href="/contact">contact page</Link>. Please do
                not include passwords, mailbox contents, or unnecessary sensitive
                information.
              </p>
            </section>
          </article>
        </div>
      </section>
      <section className="legal-related">
        <div className="container">
          <h2>Related documents</h2>
          <div>
            {legalDocuments
              .filter((related) => related.path !== document.path)
              .slice(0, 4)
              .map((related) => (
                <Link href={related.path} key={related.path}>
                  {related.shortTitle}<ArrowRight aria-hidden="true" />
                </Link>
              ))}
          </div>
        </div>
      </section>
    </main>
  );
}

function LegalRoute({ path }: { path: string }) {
  const document = legalDocumentByPath.get(path);
  return document ? <LegalPage document={document} /> : <NotFoundPage />;
}

function NotFoundPage() {
  return (
    <main className="not-found">
      <div className="container">
        <Mail />
        <h1>That page hasn't arrived.</h1>
        <p>The address may have changed or no longer exists.</p>
        <ButtonLink href="/">Return home</ButtonLink>
      </div>
    </main>
  );
}

function Footer() {
  const columns = [
    ["Product", [["Email hosting", "/product"], ["Envelaro Meet", "https://meet.envelaro.com"], ["Email health check", "/tools/email-health"], ["Security", "/security"], ["Migration", "/migration"], ["Pricing", "/pricing"], ["Managed private deployment", "/managed-private-deployment"]]],
    ["Free tools", [["DMARC checker", "/tools/dmarc-checker"], ["DKIM checker", "/tools/dkim-checker"], ["SPF checker", "/tools/spf-checker"], ["Domain blocklist", "/tools/domain-blocklist-checker"], ["IP blocklist", "/tools/ip-blocklist-checker"]]],
    ["Company", [["About", "/about"], ["FAQ", "/faq"], ["Contact", "/contact"], ["Status", "/status"]]],
    ["Legal", [["Privacy", "/privacy"], ["Website terms", "/terms"], ["Service terms", "/service-terms"], ["Billing and refunds", "/billing-policy"], ["Acceptable use", "/acceptable-use"], ["Cookies", "/cookies"]]],
    ["Trust", [["Managed service model", "/service-model"], ["Data processing", "/dpa"], ["Data lifecycle", "/data-lifecycle"], ["Subprocessors", "/subprocessors"], ["Service availability", "/service-availability"]]],
  ] as const;
  return (
    <footer className="site-footer">
      <div className="container site-footer__grid">
        <div className="footer-brand">
          <Brand />
          <p>Envelaro  -  Dependable business email.</p>
          <a href="mailto:support@envelaro.com">support@envelaro.com</a>
        </div>
        {columns.map(([title, links]) => (
          <div className="footer-column" key={title}>
            <h2>{title}</h2>
            {links.map(([label, href]) => href.startsWith("https://") ? <a href={href} key={label}>{label}</a> : <Link href={href} key={label}>{label}</Link>)}
          </div>
        ))}
      </div>
      <div className="container site-footer__bottom">
        <span>© {new Date().getFullYear()} Envelaro</span>
        <span>Built for teams worldwide.</span>
      </div>
    </footer>
  );
}

function ScrollManager() {
  const [location] = useLocation();
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      const frame = window.requestAnimationFrame(() => {
        document.getElementById(hash)?.scrollIntoView({ block: "start" });
      });
      return () => window.cancelAnimationFrame(frame);
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location]);
  return null;
}

function WebsiteVisitTracker() {
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      void recordWebsiteVisit();
    }, 0);
    return () => window.clearTimeout(timeout);
  }, []);
  return null;
}

export function App() {
  return (
    <div className="site-shell">
      <SiteMetadataManager />
      <StructuredData />
      <ScrollManager />
      <WebsiteVisitTracker />
      <MarketingControls />
      <Header />
      <EvaluationAnnouncement />
      <Switch>
        <WouterRoute path="/" component={HomePage} />
        <WouterRoute path="/product" component={ProductPage} />
        <WouterRoute path="/pricing" component={PricingPage} />
        <WouterRoute path="/small-business-email-hosting"><SolutionLandingPage content={solutionLandingContent.smallBusiness} /></WouterRoute>
        <WouterRoute path="/custom-domain-email-hosting"><SolutionLandingPage content={solutionLandingContent.customDomain} /></WouterRoute>
        <WouterRoute path="/multiple-domain-email-hosting"><SolutionLandingPage content={solutionLandingContent.multipleDomain} /></WouterRoute>
        <WouterRoute path="/managed-email-setup"><SolutionLandingPage content={solutionLandingContent.managedSetup} /></WouterRoute>
        <WouterRoute path="/managed-private-deployment" component={ManagedPrivateDeploymentPage} />
        <WouterRoute path="/security" component={SecurityPage} />
        <WouterRoute path="/migration" component={MigrationPage} />
        <WouterRoute path="/migration/cpanel"><ProviderMigrationPage content={providerMigrationContent.cpanel} /></WouterRoute>
        <WouterRoute path="/migration/godaddy"><ProviderMigrationPage content={providerMigrationContent.godaddy} /></WouterRoute>
        <WouterRoute path="/migration/google-workspace"><ProviderMigrationPage content={providerMigrationContent.googleWorkspace} /></WouterRoute>
        <WouterRoute path="/migration/microsoft-365"><ProviderMigrationPage content={providerMigrationContent.microsoft365} /></WouterRoute>
        <WouterRoute path="/migration/zoho-mail"><ProviderMigrationPage content={providerMigrationContent.zohoMail} /></WouterRoute>
        <WouterRoute path="/about" component={AboutPage} />
        <WouterRoute path="/status" component={StatusPage} />
        <WouterRoute path="/faq" component={FaqPage} />
        <WouterRoute path="/tools/email-health" component={EmailHealthCheckPage} />
        <WouterRoute path="/tools/dmarc-checker"><RecordCheckerPage kind="dmarc" /></WouterRoute>
        <WouterRoute path="/tools/dkim-checker"><RecordCheckerPage kind="dkim" /></WouterRoute>
        <WouterRoute path="/tools/spf-checker"><RecordCheckerPage kind="spf" /></WouterRoute>
        <WouterRoute path="/tools/domain-blocklist-checker"><BlocklistCheckerPage kind="domain" /></WouterRoute>
        <WouterRoute path="/tools/ip-blocklist-checker"><BlocklistCheckerPage kind="ip" /></WouterRoute>
        <WouterRoute path="/resources" component={ResourcesPage} />
        {RESOURCE_GUIDES.map((guide) => (
          <WouterRoute path={guide.path} key={guide.path}>
            <ResourceGuidePage guide={guide} />
          </WouterRoute>
        ))}
        <WouterRoute path="/contact" component={ContactPage} />
        <WouterRoute path="/evaluation" component={EvaluationPage} />
        <WouterRoute path="/privacy"><LegalRoute path="/privacy" /></WouterRoute>
        <WouterRoute path="/terms"><LegalRoute path="/terms" /></WouterRoute>
        <WouterRoute path="/acceptable-use"><LegalRoute path="/acceptable-use" /></WouterRoute>
        <WouterRoute path="/cookies"><LegalRoute path="/cookies" /></WouterRoute>
        <WouterRoute path="/dpa"><LegalRoute path="/dpa" /></WouterRoute>
        <WouterRoute path="/subprocessors"><LegalRoute path="/subprocessors" /></WouterRoute>
        <WouterRoute path="/service-availability"><LegalRoute path="/service-availability" /></WouterRoute>
        <WouterRoute path="/service-model"><LegalRoute path="/service-model" /></WouterRoute>
        <WouterRoute path="/service-terms"><LegalRoute path="/service-terms" /></WouterRoute>
        <WouterRoute path="/billing-policy"><LegalRoute path="/billing-policy" /></WouterRoute>
        <WouterRoute path="/data-lifecycle"><LegalRoute path="/data-lifecycle" /></WouterRoute>
        <WouterRoute component={NotFoundPage} />
      </Switch>
      <Footer />
    </div>
  );
}





